import h from "solid-js/h";
import { render } from "solid-js/web";
import { createSignal, createMemo, createEffect, onMount, onCleanup } from "solid-js";
import "./theme.css";

/* ---------------- tiny API helper (cookies included) ---------------- */
const api = {
  async get(path: string) {
    const r = await fetch(path, { credentials: "same-origin" });
    if (r.status === 401) return { _unauth: true };
    if (!r.ok) throw new Error((await r.text()) || "HTTP " + r.status);
    return r.json();
  },
  async send(path: string, method: string, body: any) {
    const r = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const t = await r.text();
    if (!r.ok) throw new Error(t || "HTTP " + r.status);
    return t ? JSON.parse(t) : {};
  },
  // field defaults to "file" — the name every importer uses; the logo upload is
  // the one endpoint that wants something else.
  async upload(path: string, file: File, field = "file") {
    const fd = new FormData();
    fd.append(field, file);
    const r = await fetch(path, { method: "POST", credentials: "same-origin", body: fd });
    if (!r.ok) throw new Error((await r.text()) || "HTTP " + r.status);
    return r.json();
  },
};


// importErrorList renders the rejected-rows panel shared by the opname and
// klasifikasi Excel importers. Capped at 100 here as well as server-side —
// opname sends its full list, so the cap has to live on this end too;
// error_count carries the real total either way.
function importErrorList(result: () => any) {
  return () => (result() && result().errors && result().errors.length)
    ? h("div", { style: "margin-top:12px;border-top:1px solid var(--line);padding-top:10px" }, [
        h("div", { class: "hint", style: "margin:0 0 6px;color:#b5453b" },
          result().error_count + " baris ditolak (perbaiki di Excel & import ulang):"),
        h("div", { style: "max-height:150px;overflow:auto;font-size:12.5px;font-family:monospace" },
          result().errors.slice(0, 100).map((er: any) => h("div", null,
            "Baris " + er.row + (er.code ? " (" + er.code + ")" : "") + " \u2014 " + er.msg))),
      ]) : null;
}
// exportXlsx sends a table the page already has to the server, which returns a
// real .xlsx download. Generic so any table can reuse it.
async function exportXlsx(filename: string, headers: string[], rows: any[][]) {
  try {
    const r = await fetch("/api/export/xlsx", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename, sheet: filename.slice(0, 28), headers, rows }),
    });
    if (!r.ok) throw new Error((await r.text()) || "HTTP " + r.status);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename.endsWith(".xlsx") ? filename : filename + ".xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e: any) { alert("Export gagal: " + e.message); }
}
// xlsxBtn: reusable "⤓ Excel" button. filenameFn/rowsFn are evaluated on click.
function xlsxBtn(filenameFn: () => string, headers: string[], rowsFn: () => any[][]) {
  return h("button", { class: "btn btn-ghost", style: "padding:6px 12px;font-size:12.5px",
    onClick: () => { const rs = rowsFn(); if (!rs.length) { alert("Tidak ada data untuk diekspor."); return; } exportXlsx(filenameFn(), headers, rs); } }, "⤓ Excel");
}
const jenisText = (j: string) => j === "raw" ? "Bahan Baku" : j === "semi" ? "Setengah Jadi" : "Barang Jadi";
// <option>s for a Jenis filter select — "Semua" + raw/semi/finished, labels from jenisText.
const jenisOptions = (sig: () => string) => [
  h("option", { value: "", selected: () => sig() === "" }, "Semua Jenis"),
  ...["raw", "semi", "finished"].map((j) => h("option", { value: j, selected: () => sig() === j }, jenisText(j))),
];
const dstamp = () => { const d = new Date(); return "" + d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0"); };
// Local calendar date as YYYY-MM-DD — for date inputs and date filters.
const todayISO = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };

// THE Surat Jalan kind table: one row per kind drives the builder select, the
// list filter, the badge and the label, so the three can never disagree.
// manualLines marks the kind whose lines are keyed in by hand (and therefore
// needs a destination + source-gudang picker).
// word/detailHead/detailOf/empty drive the per-cabang "Susun SJ" table, which is
// otherwise identical for both aggregating kinds.
const SJ_KINDS = [
  { code: "permintaan", label: "Permintaan Display", cls: "cabang", manualLines: false,
    word: "permintaan", detailHead: "Isi", empty: "Tidak ada permintaan terbuka.",
    detailOf: (r: any) => r.request_no },
  { code: "pesanan", label: "Pesanan Pelanggan", cls: "pusat", manualLines: false,
    word: "pesanan", detailHead: "Pelanggan", empty: "Tidak ada pesanan yang siap dikirim dalam jangkauan H-n.",
    detailOf: (p: any) => p.no_po + (p.cust_name ? " (" + p.cust_name + ")" : "") },
  { code: "bahan_baku", label: "Bahan Baku", cls: "gold", manualLines: true,
    word: "item", detailHead: "", empty: "", detailOf: (x: any) => x.item },
];
const SJ_KIND: any = Object.fromEntries(SJ_KINDS.map((k) => [k.code, k]));
const kindLabel = (k: string) => SJ_KIND[k]?.label || k;
// Ambil/Kirim is the field the outlet routes goods by, so screen, picker and
// printed sheet all say it the same way.
const kirimLabel = (k: string) => k === "Kirim" ? "Dikirim ke alamat konsumen" : "Diambil konsumen di outlet";
const kirimBadge = (o: any) => o.jenis_kirim === "Kirim"
  ? h("span", { class: "badge pusat", style: "margin-left:6px", title: o.cust_addr || "" }, "Kirim")
  : h("span", { class: "badge", style: "margin-left:6px" }, "Ambil");

/* ---------------- global reactive state ---------------- */
const [me, setMe] = createSignal<any>(null);
// Role/branch accessors — pusat_production = production line; cabang_user =
// outlet staff at ANY outlet (incl. Pusat's); Finishing only at cabang-type.
// owner is a superset of admin — it sees and does everything admin does, plus
// cancelling a committed production (isOwnerRole gates only that).
const isAdminRole = () => me()?.role === "admin" || me()?.role === "owner";
const isOwnerRole = () => me()?.role === "owner";
const isProdRole = () => me()?.role === "pusat_production";
const isOutletRole = () => me()?.role === "cabang_user";
// "Minimal = Standar": when on, the stored Min is ignored (par is the minimum)
// and the column/field disappears everywhere it would otherwise be shown. With
// Min hidden, "Par" alone reads oddly — it is the standard, so it is renamed.
// All four evaluate eagerly; settings load before first render (Root gates on
// ready()), and a change lands on the next navigation.
const minEqPar = () => settings().display_min_equals_par === true;
// "Izinkan SJ susulan": a cabang may get a second numbered SJ on a day whose
// document already went out, instead of the sources waiting for tomorrow.
const extraSJ = () => settings().allow_extra_sj_per_day === true;
const parLabel = () => minEqPar() ? "Standar" : "Par";
// The prose form of parLabel, for sentences that name the pair rather than a
// column. Goes through parLabel so "Standar" stays spelled in exactly one place.
const minParLabel = () => minEqPar() ? parLabel() : "min/par";
const minTh = () => minEqPar() ? null : h("th", { style: "text-align:right" }, "Min");
const minTd = (v: any) => minEqPar() ? null : numTd(v);
const parTh = () => h("th", { style: "text-align:right" }, parLabel());
// A finishing task whose setengah-jadi does not cover the shortfall. Derived,
// not a server flag, so it can never disagree with the two numbers it compares.
// Finishing still proceeds — the semi goes negative and the next permintaan
// reorders it — so this is a warning, never a block. SEMI_INK is the semi
// colour used by jenisBadge, so the warning tracks the badge.
const SEMI_INK = "#A9781F";
const semiShort = (t: any) => +t.semi_cap < +t.shortfall;
const semiShortText = (t: any) => {
  const bind = t.bind || "Setengah jadi";
  return "⚠ " + bind + " hanya cukup untuk " + fmt(t.semi_cap) + " dari " + fmt(t.shortfall) +
    " — boleh dilanjutkan, stok " + bind + " akan minus " + fmt(+t.shortfall - +t.semi_cap) + ".";
};
// Capability accessors (admin can do everything).
const canProduce = () => isProdRole() || isAdminRole();
const canOutlet = () => isOutletRole() || isAdminRole();
// Finishing is the one gate keyed on BRANCH TYPE, not just role: Pusat's own
// outlet staff never finish (the production line does semi→jadi there); only
// outlet staff at a cabang-type branch do.
const canFinish = () => isOutletRole() && me()?.branch_type === "cabang";
const [settings, setSettings] = createSignal<any>({ company_name: "Ajeng Bakery", tagline: "", has_logo: false });
const [logoV, setLogoV] = createSignal(1); // cache-buster for /api/logo
const [route, setRoute] = createSignal(location.hash.replace("#", "") || "/");
window.addEventListener("hashchange", () => setRoute(location.hash.replace("#", "") || "/"));
const go = (p: string) => (location.hash = p);
const [navOpen, setNavOpen] = createSignal(false); // mobile/tablet drawer

async function loadSettings() {
  try { setSettings(await api.get("/api/settings")); } catch (_) {}
}
async function loadMe() {
  const r = await api.get("/api/me");
  setMe(r && r._unauth ? null : r);
}

/* ---------------- live refresh over SSE (no polling) ---------------- */
type LiveSub = { topics: string[]; fn: () => void };
const liveSubs = new Set<LiveSub>();
// onLive registers a refresh callback for the given topics for the lifetime of
// the calling component (auto-unsubscribes on cleanup when the page unmounts).
function onLive(topics: string[], fn: () => void) {
  const s: LiveSub = { topics, fn };
  liveSubs.add(s);
  onCleanup(() => liveSubs.delete(s));
}
let liveStarted = false;
function startLive() {
  if (liveStarted || typeof EventSource === "undefined") return;
  liveStarted = true;
  const es = new EventSource("/api/events"); // browser auto-reconnects on drop
  // One user action often broadcasts several topics back-to-back; coalesce a
  // burst so each subscriber refetches once, not once per topic.
  let pending = new Set<string>();
  let timer: any = null;
  es.addEventListener("change", (e: any) => {
    pending.add(e.data);
    if (timer) return;
    timer = setTimeout(() => {
      const topics = pending; pending = new Set(); timer = null;
      liveSubs.forEach((s) => {
        if (s.topics.some((t: string) => topics.has(t))) { try { s.fn(); } catch (_) {} }
      });
    }, 60);
  });
}

/* ---------------- inline SVG icons (no external assets) ---------------- */
const svg = (paths: any, extra?: any) =>
  h("svg", { class: "ic", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round", ...(extra || {}) }, paths);
const IcDash = () => svg([h("rect",{x:3,y:3,width:8,height:8,rx:2}),h("rect",{x:13,y:3,width:8,height:5,rx:2}),h("rect",{x:13,y:10,width:8,height:11,rx:2}),h("rect",{x:3,y:13,width:8,height:8,rx:2})]);
const IcStore = () => svg([h("path",{d:"M3 9l1.2-4.2A2 2 0 016.1 3h11.8a2 2 0 011.9 1.8L21 9"}),h("path",{d:"M4 9v10a1 1 0 001 1h14a1 1 0 001-1V9"}),h("path",{d:"M3 9h18"}),h("path",{d:"M9 20v-5h6v5"})]);
const IcGear = () => svg([h("circle",{cx:12,cy:12,r:3}),h("path",{d:"M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.7.7 1.6 1.6 0 01-3.2 0 1.6 1.6 0 00-2.7-.7l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00-.7-2.7 1.6 1.6 0 010-3.2 1.6 1.6 0 00.7-2.7l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H9a1.6 1.6 0 001-1.5 1.6 1.6 0 013.2 0 1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1 1.6 1.6 0 010 3.2 1.6 1.6 0 00-1.5 1z"})]);
const IcOut = () => svg([h("path",{d:"M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"}),h("path",{d:"M16 17l5-5-5-5"}),h("path",{d:"M21 12H9"})]);
const IcBox = () => svg([h("path",{d:"M21 8l-9-5-9 5 9 5 9-5z"}),h("path",{d:"M3 8v8l9 5 9-5V8"}),h("path",{d:"M12 13v8"})]);
const IcUsers = () => svg([h("path",{d:"M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"}),h("circle",{cx:9,cy:7,r:4}),h("path",{d:"M23 21v-2a4 4 0 00-3-3.87"}),h("path",{d:"M16 3.13a4 4 0 010 7.75"})]);
const IcClip = () => svg([h("rect",{x:5,y:4,width:14,height:17,rx:2}),h("path",{d:"M9 4h6v2H9z"}),h("path",{d:"M9 11h6"}),h("path",{d:"M9 15h4"})]);
const IcInbox = () => svg([h("path",{d:"M22 12h-6l-2 3h-4l-2-3H2"}),h("path",{d:"M5 5h14l3 7v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6z"})]);
const IcUpload = () => svg([h("path",{d:"M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"}),h("path",{d:"M7 9l5-5 5 5"}),h("path",{d:"M12 4v12"})], { class: "ic", width: 26, height: 26 });
const IcRecipe = () => svg([h("path",{d:"M4 4a2 2 0 012-2h12a1 1 0 011 1v18a1 1 0 01-1 1H6a2 2 0 01-2-2z"}),h("path",{d:"M4 18a2 2 0 012-2h13"}),h("path",{d:"M8 6h7"}),h("path",{d:"M8 9.5h7"})]);
const IcLock = () => svg([h("rect",{x:4,y:11,width:16,height:9,rx:2}),h("path",{d:"M8 11V7a4 4 0 018 0v4"})]);
const IcTune = () => svg([h("line",{x1:4,y1:7,x2:20,y2:7}),h("line",{x1:4,y1:12,x2:20,y2:12}),h("line",{x1:4,y1:17,x2:20,y2:17}),h("circle",{cx:9,cy:7,r:2.2}),h("circle",{cx:15,cy:12,r:2.2}),h("circle",{cx:8,cy:17,r:2.2})]);
const IcTruck = () => svg([h("path",{d:"M3 6h11v9H3z"}),h("path",{d:"M14 9h4l3 3v3h-7z"}),h("circle",{cx:7,cy:18,r:1.8}),h("circle",{cx:17,cy:18,r:1.8})]);
const IcFactory = () => svg([h("path",{d:"M3 21V9l5 3V9l5 3V6l5 3v12z"}),h("path",{d:"M3 21h18"})]);
const IcCart = () => svg([h("path",{d:"M6 7h12l1.5 13a1 1 0 01-1 1.1H5.5a1 1 0 01-1-1.1z"}),h("path",{d:"M9 10V6a3 3 0 016 0v4"})]);
const IcReport = () => svg([h("path",{d:"M5 3h9l5 5v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"}),h("path",{d:"M14 3v5h5"}),h("path",{d:"M8 13v4"}),h("path",{d:"M12 11v6"}),h("path",{d:"M16 15v2"})]);
/* croissant-ish default brand mark */
const Croissant = () =>
  h("svg", { viewBox: "0 0 24 24", width: 26, height: 26, fill: "none", stroke: "currentColor", "stroke-width": 1.7, "stroke-linecap": "round", "stroke-linejoin": "round" },
    [h("path",{d:"M3 16c3 2 6 2 9 0"}),h("path",{d:"M12 16c3 2 6 2 9 0"}),h("path",{d:"M4 15l3-6 5 5 5-5 3 6"}),h("path",{d:"M12 14V8"})]);

/* ---------------- brand mark (logo or fallback) ---------------- */
function BrandMark() {
  return h("span", { class: "mark" }, () =>
    settings().has_logo
      ? h("img", { src: () => "/api/logo?v=" + logoV(), alt: "logo" })
      : Croissant()
  );
}

/* ---------------- sidebar ---------------- */
const IcMenu = () => svg([h("line",{x1:4,y1:7,x2:20,y2:7}),h("line",{x1:4,y1:12,x2:20,y2:12}),h("line",{x1:4,y1:17,x2:20,y2:17})]);
function NavItem(p: { to: string; icon: any; label: string }) {
  return h("a", {
    class: () => "nav-item" + (route() === p.to ? " active" : ""),
    href: "#" + p.to,
    onClick: () => setNavOpen(false),
  }, p.icon(), h("span", null, p.label));
}
function Sidebar() {
  return h("aside", { class: () => "sidebar" + (navOpen() ? " open" : "") }, [
    h("div", { class: "sidebar-top" }, [
      h("div", { class: "brand" }, BrandMark(),
        h("span", { class: "name" }, () => settings().company_name || "Ajeng Bakery",
          h("small", null, "Central"))),
      h("button", { class: "hamburger", "aria-label": "Menu", onClick: () => setNavOpen(!navOpen()) }, IcMenu()),
    ]),
    h("div", { class: "nav-label" }, "Menu"),
    NavItem({ to: "/", icon: IcDash, label: "Dashboard" }),
    () => isAdminRole() ? NavItem({ to: "/branches", icon: IcStore, label: "Outlet" }) : null,
    NavItem({ to: "/stock", icon: IcBox, label: "Posisi Stok" }),
    h("div", { class: "nav-label" }, () => isOutletRole() ? "Outlet" : "Produksi"),
    NavItem({ to: "/requests", icon: IcClip, label: "Permintaan" }),
    NavItem({ to: "/pesanan", icon: IcCart, label: "Pesanan Pelanggan" }),
    // Production line gets Produksi; cabang outlet staff get the same page as
    // "Finishing" (semi → jadi).
    () => canProduce() || canFinish()
      ? NavItem({ to: "/produksi", icon: IcFactory, label: canProduce() ? "Produksi" : "Finishing" }) : null,
    () => canProduce() ? NavItem({ to: "/suratjalan", icon: IcTruck, label: "Surat Jalan" }) : null,
    () => canOutlet() ? NavItem({ to: "/terima", icon: IcInbox, label: "Terima Produksi" }) : null,
    () => canProduce() ? NavItem({ to: "/recipes", icon: IcRecipe, label: "Resep Produk" }) : null,
    () => canProduce() ? NavItem({ to: "/laporan", icon: IcReport, label: "Laporan Produksi" }) : null,
    () => canOutlet() ? NavItem({ to: "/mutasi", icon: IcBox, label: "Mutasi Stock" }) : null,
    NavItem({ to: "/thresholds", icon: IcTune, label: "Batas Stok" }),
    h("div", { class: "nav-label" }, "Sistem"),
    () => isAdminRole() ? NavItem({ to: "/users", icon: IcUsers, label: "Pengguna" }) : null,
    NavItem({ to: "/settings", icon: IcGear, label: "Pengaturan" }),
    h("div", { class: "spacer" }),
    h("div", { class: "who" },
      h("span", { class: "ava" }, () => (me()?.username || "?").slice(0, 1).toUpperCase()),
      h("span", { class: "meta" }, h("b", null, () => me()?.username || ""),
        h("br"), h("span", null, () => roleLabel(me()?.role || "") + (me()?.branch ? " · " + me().branch : "")))),
    h("a", { class: "nav-item", href: "#", onClick: (e: any) => { setNavOpen(false); doLogout(e); } }, IcOut(), h("span", null, "Keluar")),
  ]);
}

async function doLogout(e: Event) {
  e.preventDefault();
  await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
  setMe(null);
}

/* ---------------- pages ---------------- */
function Topbar(p: { title: string; sub?: string }) {
  return h("div", { class: "topbar" },
    h("div", null, h("div", { class: "title" }, p.title), p.sub ? h("div", { class: "sub" }, p.sub) : null));
}

/* ------- number formatting + jenis badge (shared) ------- */
const fmt = (n: any) => {
  const v = Number(n || 0);
  return (Math.round(v * 100) / 100).toLocaleString("id-ID");
};
// Right-aligned numeric table cell. Pass a function for a value that changes
// (a totals row over a filtered list); it stays reactive instead of the caller
// re-spelling this style string to keep its own thunk.
const numTd = (v: any, extra?: string) => h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" + (extra || "") },
  typeof v === "function" ? () => fmt(v()) : fmt(v));
// Aggregate model: production is no longer per-permintaan — what matters is
// WHERE it happened (Pusat produces, cabang finishes semi→jadi).
const jenisProduksi = (r: any) => r.branch_type === "cabang" ? "Finishing" : "Produksi";
const jenisProduksiBadge = (r: any) => h("span", {
  class: "badge " + (r.branch_type === "cabang" ? "gold" : "pusat"),
  title: r.request_no ? "Permintaan lama: " + r.request_no : "" }, jenisProduksi(r));
// Document-line chip: bold code, product name, × qty.
// THE document-line chip, shared by Permintaan and Surat Jalan. `skin` appends
// to the style (leading ";" like numTd's extra), `extra` appends children — that
// is how the Permintaan page shows withdrawal state without a second chip.
const lineBadge = (l: any, skin?: string, extra?: any[]) =>
  h("span", { class: "badge", style: "background:var(--surface-2);color:var(--ink-soft);margin-right:6px;margin-bottom:4px" + (skin || "") },
    h("b", null, l.item), (l.name ? " " + l.name : "") + " × " + fmt(l.qty), ...(extra || []));
function jenisBadge(j: string) {
  const label = j === "raw" ? "Bahan Baku" : j === "semi" ? "Setengah Jadi" : "Barang Jadi";
  const style = j === "raw"
    ? "background:var(--sage-soft);color:var(--sage)"
    : j === "semi"
    ? "background:#FBEECB;color:#A9781F"
    : "background:var(--brand-soft);color:var(--brand-strong)";
  return h("span", { class: "badge", style }, label);
}

/* ---------------- item autocomplete (select2-style, searches code+name, shows stock) ---------------- */
function ItemSelect(p: { value: () => string; onSelect: (code: string, name?: string) => void; placeholder?: string; branch?: () => string; style?: string; mode?: string }) {
  const [query, setQuery] = createSignal("");
  const [results, setResults] = createSignal<any[]>([]);
  const [open, setOpen] = createSignal(false);
  const [active, setActive] = createSignal(-1);
  let timer: any;
  const search = (q: string) => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        const url = "/api/items?q=" + encodeURIComponent(q) + (p.branch && p.branch() ? "&branch=" + encodeURIComponent(p.branch()) : "") + (p.mode ? "&mode=" + p.mode : "");
        setResults((await api.get(url)) || []); setActive(-1);
      } catch (_) { setResults([]); }
    }, 150);
  };
  const commit = (code: string, name?: string) => { p.onSelect(code, name); setQuery(""); setOpen(false); setActive(-1); };
  const pick = (it: any) => commit(it.code, it.name);
  onCleanup(() => clearTimeout(timer));
  return h("div", { class: "combo", style: p.style || "" }, [
    h("input", { class: "input",
      placeholder: p.placeholder || "Cari kode / nama…",
      value: () => open() ? query() : (p.value() || ""),
      onInput: (e: any) => { setQuery(e.currentTarget.value.toUpperCase()); setOpen(true); search(e.currentTarget.value); },
      onFocus: () => { setOpen(true); search(query()); },
      onBlur: () => setTimeout(() => setOpen(false), 160),
      onKeyDown: (e: any) => {
        const rs = results();
        if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive(Math.min(active() + 1, rs.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive(Math.max(active() - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); if (open() && rs.length) pick(rs[active() >= 0 ? active() : 0]); else if (query()) commit(query()); }
        else if (e.key === "Escape") { setOpen(false); }
      } }),
    () => open() && results().length ? h("div", { class: "combo-list" },
      results().map((it: any, i: number) => h("div", {
        class: "combo-opt" + (i === active() ? " active" : ""),
        onMouseDown: (e: any) => { e.preventDefault(); pick(it); } }, [
        h("span", null, h("b", null, it.code), " ", h("span", { class: "muted" }, it.name)),
        h("span", { class: "combo-stock" }, "stok " + fmt(it.stock)),
      ]))) : null,
  ]);
}

function Dashboard() {
  const [branches, setBranches] = createSignal<any[]>([]);
  const [sum, setSum] = createSignal<any>({});
  const loadSum = async () => { try { setSum(await api.get("/api/summary")); } catch (_) {} };
  const loadBranches = async () => { try { setBranches(await api.get("/api/branches")); } catch (_) {} };
  onMount(async () => { await loadSum(); await loadBranches(); });
  onLive(["stock", "requests", "deliveries"], loadSum);
  onLive(["branches"], () => { loadBranches(); loadSum(); });

  const scope = () => sum().scope;
  const n = (k: string) => () => String(sum()[k] ?? 0);
  const qbtn = (label: string, to: string) =>
    h("a", { class: "btn", href: "#" + to, style: "text-decoration:none" }, label);

  const adminCards = () => [
    Stat(IcStore, n("branches"), "Total Outlet", ""),
    Stat(IcStore, n("pusat"), "Pusat", "gold"),
    Stat(IcStore, n("cabang"), "Cabang", "sage"),
    Stat(IcBox, n("items"), "Item Produk", ""),
    Stat(IcClip, n("pending_requests"), "Permintaan Baru", "gold"),
    Stat(IcBox, n("low_stock"), "BB Menipis", "gold"),
  ];
  const prodCards = () => [
    Stat(IcClip, n("pending_requests"), "Permintaan Masuk", "gold"),
    Stat(IcBox, n("items"), "Item Produk", ""),
    Stat(IcBox, n("low_stock"), "BB Menipis", "gold"),
  ];
  const outletCards = () => [
    Stat(IcBox, n("items"), "Item di Outlet", ""),
    Stat(IcInbox, n("incoming_deliveries"), "Kiriman Masuk", "gold"),
    Stat(IcClip, n("below_min"), "Di Bawah Minimum", "gold"),
    Stat(IcClip, n("pending_requests"), "Permintaan Saya", "sage"),
    Stat(IcBox, n("low_stock"), "BB Menipis", "gold"),
  ];
  // Card set follows the ROLE, not the branch type: Pusat's own outlet staff
  // (cabang_user @ pusat branch) are outlet users like any cabang's.
  const cards = () =>
    scope() === "all" ? adminCards() : isProdRole() ? prodCards() : outletCards();

  return h("div", { class: "main" }, Topbar({ title: "Dashboard", sub: "Ringkasan sistem produksi" }),
    h("div", { class: "content" }, [
      () => scope() === "branch" ? h("div", { class: "card", style: "margin-bottom:18px;display:flex;align-items:center;gap:14px" }, [
        h("span", { class: "chip gold" }, IcStore()),
        h("div", null,
          h("div", { style: "font-weight:700;font-size:16px" }, sum().branch_name || "—", " ",
            h("span", { class: "badge " + (me()?.branch_type || "cabang") }, me()?.branch_type === "pusat" ? "Pusat" : "Cabang")),
          h("div", { class: "muted", style: "font-size:12.5px" }, "Ringkasan Anda · " + roleLabel(me()?.role))),
      ]) : null,

      () => scope() ? h("div", { class: "grid stat-grid" }, cards()) : null,

      // admin: full branch registry; branch users: quick actions
      () => scope() === "all"
        ? h("div", { class: "card", style: "margin-top:22px" },
            h("h3", null, "Outlet Terdaftar"),
            h("div", { class: "hint" }, "Outlet yang terhubung ke server pusat."),
            h("table", { class: "table" },
              h("thead", null, h("tr", null, h("th", null, "Kode"), h("th", null, "Nama"), h("th", null, "Jenis"))),
              h("tbody", null, () => branches().map((b) =>
                h("tr", null,
                  h("td", null, h("b", null, b.code)),
                  h("td", null, b.name),
                  h("td", null, h("span", { class: "badge " + b.type }, b.type === "pusat" ? "Pusat" : "Cabang")))))))
        : (() => scope() === "branch" ? h("div", { class: "card", style: "margin-top:22px" }, [
            h("h3", null, "Tindakan Cepat"),
            h("div", { class: "hint" }, "Pintasan ke tugas Anda."),
            h("div", { class: "row", style: "gap:10px;flex-wrap:wrap;margin-top:12px" },
              () => isProdRole()
                ? [qbtn("Lihat Permintaan", "/requests"), qbtn("Posisi Stok", "/stock"), qbtn("Resep Produk", "/recipes")]
                : [qbtn("Buat Permintaan", "/requests"), qbtn("Terima Produksi", "/terima"), qbtn("Posisi Stok", "/stock")]),
          ]) : null),
    ]));
}
function Stat(icon: any, value: any, label: string, tone: string) {
  return h("div", { class: "stat" },
    h("span", { class: "chip " + tone }, icon()),
    h("div", null, h("div", { class: "num" }, value), h("div", { class: "lbl" }, label)));
}

function Settings() {
  const [name, setName] = createSignal(settings().company_name || "");
  const [tag, setTag] = createSignal(settings().tagline || "");
  const [msg, setMsg] = createSignal<any>(null);
  const [busy, setBusy] = createSignal(false);
  const [over, setOver] = createSignal(false);
  let fileInput: any;
  // Suggestion-engine knobs (admin): the pesanan horizon + whether pesanan
  // demand joins the saran at all.
  const [horizon, setHorizon] = createSignal(String(settings().pesanan_horizon_days ?? 3));
  const [inclPes, setInclPes] = createSignal(settings().saran_include_pesanan !== false);
  const [allowJadi, setAllowJadi] = createSignal(settings().allow_cabang_order_finished === true);
  // How many days ahead a pre-order may ride today's Surat Jalan (H-n).
  const [sjHorizon, setSjHorizon] = createSignal(String(settings().sj_pesanan_horizon_days ?? 1));
  const [minPar, setMinPar] = createSignal(settings().display_min_equals_par === true);
  const [susulan, setSusulan] = createSignal(settings().allow_extra_sj_per_day === true);
  const [autoSJ, setAutoSJ] = createSignal(settings().auto_draft_sj === true);
  const [saranMsg, setSaranMsg] = createSignal<any>(null);
  const saveSaran = async () => {
    setSaranMsg(null);
    try {
      await api.send("/api/settings/saran", "POST", { horizon_days: +horizon() || 0, include_pesanan: inclPes(),
        allow_cabang_order_finished: allowJadi(), sj_pesanan_horizon_days: +sjHorizon() || 0,
        display_min_equals_par: minPar(), allow_extra_sj_per_day: susulan(), auto_draft_sj: autoSJ() });
      await loadSettings();
      setSaranMsg({ ok: true, t: "Tersimpan — saran otomatis mengikuti pengaturan baru." });
    } catch (e: any) { setSaranMsg({ ok: false, t: e.message }); }
  };
  // Finishing-di-cabang list (admin): the per-item capability the kanban rule
  // keys on. Explicit list — not recipe-derived.
  const [finItems, setFinItems] = createSignal<any[]>([]);
  const [finItem, setFinItem] = createSignal("");
  const [finMsg, setFinMsg] = createSignal<any>(null);
  const loadFin = async () => { if (!isAdminRole()) return; try { setFinItems(await api.get("/api/finishable") || []); } catch (_) {} };
  onMount(loadFin);
  const addFin = async () => {
    if (!finItem()) return; setFinMsg(null);
    try { await api.send("/api/finishable", "POST", { item: finItem() }); setFinItem(""); await loadFin(); setFinMsg({ ok: true, t: "Ditambahkan." }); }
    catch (e: any) { setFinMsg({ ok: false, t: e.message }); }
  };
  const delFin = async (it: string) => {
    setFinMsg(null);
    try { await api.send("/api/finishable/delete", "POST", { item: it }); await loadFin(); }
    catch (e: any) { setFinMsg({ ok: false, t: e.message }); }
  };
  // Klasifikasi Excel round-trip (admin): the client edits Kategori / Gramasi /
  // Jenis / WajibH1 in a spreadsheet, and the import queues ONE command that
  // Pusat's agent applies to Delphi's tm_barang. Central's own item list is not
  // touched here — it refreshes from the snapshot the agent pushes right after.
  const [klasBusy, setKlasBusy] = createSignal(false);
  const [klasMsg, setKlasMsg] = createSignal<any>(null);
  const [klasResult, setKlasResult] = createSignal<any>(null);
  const importKlas = async (file: any) => {
    if (!file) return;
    setKlasBusy(true); setKlasMsg(null); setKlasResult(null);
    try {
      const j = await api.upload("/api/klasifikasi/import", file);
      setKlasResult(j);
      setKlasMsg({ ok: j.error_count === 0, t: j.queued
        ? j.queued + " barang dikirim ke Delphi Pusat (" + j.unchanged + " tidak berubah, " + j.error_count + " ditolak). Perubahan muncul setelah agent Pusat memprosesnya — cek lagi beberapa saat."
        : "Tidak ada perubahan untuk dikirim (" + j.unchanged + " barang sama, " + j.error_count + " ditolak)." });
    } catch (e: any) { setKlasMsg({ ok: false, t: "Import gagal: " + e.message }); }
    setKlasBusy(false);
  };
  const [curPw, setCurPw] = createSignal("");
  const [newPw, setNewPw] = createSignal("");
  const [pwMsg, setPwMsg] = createSignal<any>(null);
  const changePw = async () => {
    setPwMsg(null);
    try {
      await api.send("/api/me/password", "POST", { current: curPw(), new: newPw() });
      setCurPw(""); setNewPw(""); setPwMsg({ ok: true, t: "Kata sandi diperbarui." });
    } catch (e: any) { setPwMsg({ ok: false, t: e.message }); }
  };

  const saveProfile = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.send("/api/settings", "PUT", { company_name: name(), tagline: tag() });
      await loadSettings();
      setMsg({ ok: true, t: "Profil tersimpan." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  const doUpload = async (file: File) => {
    setMsg(null);
    if (!file) return;
    try {
      await api.upload("/api/settings/logo", file, "logo");
      await loadSettings();
      setLogoV(logoV() + 1);
      setMsg({ ok: true, t: "Logo diperbarui." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };

  return h("div", { class: "main" }, Topbar({ title: "Pengaturan", sub: "Identitas perusahaan" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,

      () => isAdminRole() ? h("div", { class: "card" }, [
        h("h3", null, "Logo Perusahaan"),
        h("div", { class: "hint" }, "Tampil di seluruh aplikasi. PNG / JPG / WEBP, maks 2 MB."),
        h("div", { class: "logo-row" }, [
          h("div", { class: "logo-preview" }, () =>
            settings().has_logo ? h("img", { src: () => "/api/logo?v=" + logoV() }) : h("span", null, "Belum ada")),
          h("div", {
            class: () => "drop" + (over() ? " over" : ""),
            onClick: () => fileInput.click(),
            onDragOver: (e: any) => { e.preventDefault(); setOver(true); },
            onDragLeave: () => setOver(false),
            onDrop: (e: any) => { e.preventDefault(); setOver(false); doUpload(e.dataTransfer.files[0]); },
          }, IcUpload(), h("div", null, h("b", null, "Klik untuk unggah"), " atau seret gambar ke sini",
             h("small", null, "PNG, JPG, atau WEBP — maksimal 2 MB"))),
          h("input", { ref: (el: any) => (fileInput = el), type: "file", accept: "image/png,image/jpeg,image/webp",
            style: "display:none", onChange: (e: any) => doUpload(e.currentTarget.files[0]) }),
        ]),
      ]) : null,

      () => isAdminRole() ? h("div", { class: "card", style: "margin-top:20px" }, [
        h("h3", null, "Identitas"),
        h("div", { class: "field" }, h("label", null, "Nama Perusahaan"),
          h("input", { class: "input", value: name, onInput: (e: any) => setName(e.currentTarget.value) })),
        h("div", { class: "field" }, h("label", null, "Tagline"),
          h("input", { class: "input", value: tag, onInput: (e: any) => setTag(e.currentTarget.value) })),
        h("button", { class: "btn btn-primary", disabled: busy, onClick: saveProfile },
          () => busy() ? h("span", { class: "spin" }) : null, "Simpan Perubahan"),
      ]) : null,

      () => isAdminRole() ? h("div", { class: "card", style: "margin-top:20px" }, [
        h("h3", null, "Saran Otomatis & Pesanan"),
        h("div", { class: "hint" }, "Pesanan pelanggan ikut dihitung dalam saran permintaan bila tanggal ambilnya dalam N hari ke depan (Rencana Produksi tidak memakai N — sesinya ditentukan jam ambil, lihat pesanan_cutoff_time). N idealnya ≈ lama siklus produksi + kirim, agar barang segar — pesanan yang lebih jauh otomatis masuk hitungan saat tanggalnya mendekat. \"Surat Jalan pesanan H-n\" terpisah: menentukan seberapa jauh ke depan pesanan boleh ikut Surat Jalan hari ini (0 = hanya yang diambil hari ini, 1 = hari ini dan besok)."),
        () => saranMsg() ? h("div", { class: "alert " + (saranMsg().ok ? "ok" : "err"), style: "margin-top:10px" }, saranMsg().t) : null,
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
          field("Horizon (hari)", h("input", { class: "input", type: "number", min: 0, max: 60, style: "max-width:120px",
            value: horizon, onInput: (e: any) => setHorizon(e.currentTarget.value) })),
          field("Surat Jalan pesanan H-n", h("input", { class: "input", type: "number", min: 0, max: 60, style: "max-width:120px",
            value: sjHorizon, onInput: (e: any) => setSjHorizon(e.currentTarget.value) })),
          h("label", { style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding-bottom:8px" },
            h("input", { type: "checkbox", checked: inclPes, style: "width:18px;height:18px;cursor:pointer", onChange: (e: any) => setInclPes(e.currentTarget.checked) }),
            h("span", null, "Sertakan pesanan pelanggan dalam saran")),
          h("label", { style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding-bottom:8px" },
            h("input", { type: "checkbox", checked: allowJadi, style: "width:18px;height:18px;cursor:pointer", onChange: (e: any) => setAllowJadi(e.currentTarget.checked) }),
            h("span", null, "Izinkan cabang minta barang jadi", h("div", { class: "muted", style: "font-size:12px" }, "Munculkan pilihan \"Minta jadi ke Pusat\" per item finishing di Batas Stok"))),
          h("label", { style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding-bottom:8px" },
            h("input", { type: "checkbox", checked: minPar, style: "width:18px;height:18px;cursor:pointer", onChange: (e: any) => setMinPar(e.currentTarget.checked) }),
            h("span", null, "Minimal = Standar",
              h("div", { class: "muted", style: "font-size:12px" },
                "Abaikan nilai Min; Standar (Par) dipakai sebagai batas minimum, jadi setiap kekurangan langsung memunculkan permintaan. Kolom Min disembunyikan di semua tampilan. Nilai Min yang tersimpan tidak dihapus — matikan opsi ini dan angka lama berlaku kembali."))),
          h("label", { style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding-bottom:8px" },
            h("input", { type: "checkbox", checked: susulan, style: "width:18px;height:18px;cursor:pointer", onChange: (e: any) => setSusulan(e.currentTarget.checked) }),
            h("span", null, "Izinkan SJ susulan",
              h("div", { class: "muted", style: "font-size:12px" },
                "Kalau Surat Jalan hari ini sudah dikirim dan masih ada permintaan/pesanan menyusul, boleh dibuat SJ kedua di hari yang sama (bernomor susulan). Kalau dimatikan, yang menyusul menunggu SJ besok."))),
          h("label", { style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding-bottom:8px" },
            h("input", { type: "checkbox", checked: autoSJ, style: "width:18px;height:18px;cursor:pointer", onChange: (e: any) => setAutoSJ(e.currentTarget.checked) }),
            h("span", null, "Susun draft Surat Jalan otomatis",
              h("div", { class: "muted", style: "font-size:12px" },
                "Begitu halaman Surat Jalan dibuka, draft tiap cabang langsung disusun dari semua permintaan/pesanan yang menunggu — admin tinggal memeriksa dan menekan Kirim. Kalau dimatikan, draft disusun manual lewat tombol Susun SJ per cabang."))),
          h("button", { class: "btn btn-primary", onClick: saveSaran }, "Simpan"),
        ]),
      ]) : null,

      () => isAdminRole() ? h("div", { class: "card", style: "margin-top:20px" }, [
        h("h3", null, "Finishing di Cabang"),
        h("div", { class: "hint" }, "Item yang WAJIB difinishing sendiri oleh outlet cabang (mis. donat misis, blackforest mini). Item ini tidak pernah diminta ke pusat — kekurangan display-nya menjadi tugas finishing di outlet, dan bahan setengah jadinya punya " +
          minParLabel() + " sendiri."),
        () => finMsg() ? h("div", { class: "alert " + (finMsg().ok ? "ok" : "err"), style: "margin-top:10px" }, finMsg().t) : null,
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
          field("Kode Item", ItemSelect({ value: finItem, onSelect: (c: string) => setFinItem(c), placeholder: "Cari kode / nama…" })),
          h("button", { class: "btn btn-primary", onClick: addFin }, "Tambah"),
        ]),
        h("table", { class: "table", style: "margin-top:14px" },
          h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama"),
            h("th", null, "Ditambah oleh"), h("th", { style: "text-align:right" }, "Aksi"))),
          h("tbody", null, () => finItems().length ? finItems().map((r: any) => h("tr", null,
            h("td", null, h("b", null, r.item)), h("td", null, r.name || "—"),
            h("td", null, r.by || "—"),
            h("td", { style: "text-align:right" },
              h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => delFin(r.item) }, "Hapus"))))
            : h("tr", null, h("td", { colspan: 4, class: "muted", style: "text-align:center;padding:20px" }, "Belum ada item.")))),
      ]) : null,

      () => isAdminRole() ? h("div", { class: "card", style: "margin-top:20px;border-color:var(--brand-soft);background:#FFF8EF" }, [
        h("h3", null, "Klasifikasi Barang (Excel)"),
        h("div", { class: "hint" }, "Unduh daftar SEMUA barang lengkap dengan klasifikasi saat ini, ubah kolom Kategori / Gramasi / Jenis / WajibH1 / Jam Produksi H / Min Qty H di Excel, lalu import. Perubahan ditulis kembali ke Master Barang Delphi di outlet Pusat (bukan hanya di webapp), jadi Delphi tetap jadi sumber utama. Baris yang tidak diubah dilewati."),
        () => klasMsg() ? h("div", { class: "alert " + (klasMsg().ok ? "ok" : "err"), style: "margin-top:10px" }, klasMsg().t) : null,
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:center;margin-top:10px" }, [
          h("a", { class: "btn", href: "/api/klasifikasi/template" }, "\u2913 Unduh Excel Klasifikasi"),
          h("label", { class: "btn" + (klasBusy() ? " disabled" : ""), style: "cursor:pointer" },
            () => klasBusy() ? h("span", { class: "spin" }) : null, "\u2912 Import Excel",
            h("input", { type: "file", accept: ".xlsx", style: "display:none",
              onChange: (e: any) => { const f = e.currentTarget.files && e.currentTarget.files[0]; e.currentTarget.value = ""; importKlas(f); } })),
        ]),
        importErrorList(klasResult),
      ]) : null,

      h("div", { class: "card", style: "margin-top:20px" }, [
        h("h3", null, "Akun Saya"),
        h("div", { class: "hint" }, "Ubah kata sandi login Anda."),
        () => pwMsg() ? h("div", { class: "alert " + (pwMsg().ok ? "ok" : "err") }, pwMsg().t) : null,
        h("div", { class: "field" }, h("label", null, "Kata Sandi Saat Ini"),
          h("input", { class: "input", type: "password", value: curPw, onInput: (e: any) => setCurPw(e.currentTarget.value) })),
        h("div", { class: "field" }, h("label", null, "Kata Sandi Baru"),
          h("input", { class: "input", type: "password", value: newPw, onInput: (e: any) => setNewPw(e.currentTarget.value) })),
        h("button", { class: "btn", onClick: changePw }, "Ubah Kata Sandi"),
      ]),
    ]));
}

function PosisiStok() {
  const [rows, setRows] = createSignal<any[]>([]);
  const [branches, setBranches] = createSignal<any[]>([]);
  const [low, setLow] = createSignal<any[]>([]);
  const [branch, setBranch] = createSignal("");
  const [jenis, setJenis] = createSignal("");
  const [gd, setGd] = createSignal("");
  const [gdOpts, setGdOpts] = createSignal<string[]>([]);
  const [q, setQ] = createSignal("");
  const [loading, setLoading] = createSignal(false);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (branch()) p.set("branch", branch());
    if (jenis()) p.set("jenis", jenis());
    if (gd()) p.set("gd", gd());
    if (q()) p.set("q", q());
    try {
      const [r, l] = await Promise.all([api.get("/api/stock?" + p.toString()), api.get("/api/bahanbaku")]);
      setRows(r || []); setLow(l || []);
      setGdOpts([...new Set([...gdOpts(), ...(r || []).map((x: any) => x.gd)])].sort());
    } catch (_) {}
    setLoading(false);
  };
  onMount(async () => {
    try { setBranches(await api.get("/api/branches")); } catch (_) {}
    load();
  });
  onLive(["stock"], load);

  const [msg, setMsg] = createSignal<any>(null);
  const [syncing, setSyncing] = createSignal(false);
  // Sync Now: force every agent to push a fresh snapshot off the 30s timer.
  const syncNow = async () => {
    setSyncing(true); setMsg(null);
    try {
      const r = await api.send("/api/stock/sync", "POST", {});
      setMsg({ ok: true, t: "Sinkronisasi diminta ke " + (r.branches || 0) + " outlet — stok diperbarui sesaat lagi." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setSyncing(false);
  };
  // Sesuaikan (koreksi): admin any outlet, produksi its own — writes a Delphi
  // Koreksi Stock at the site so tm_stockbrg matches the physical count.
  const canAdjust = () => isAdminRole() || isProdRole();
  const [adjRow, setAdjRow] = createSignal<any>(null);
  const [adjQty, setAdjQty] = createSignal("");
  const [adjReason, setAdjReason] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const openAdjust = (r: any) => { setAdjRow(r); setAdjQty(String(r.qty)); setAdjReason(""); setMsg(null); };
  const saveAdjust = async () => {
    const r = adjRow(); if (!r) return;
    if (adjQty() === "" || +adjQty() < 0) { setMsg({ ok: false, t: "Stok fisik tidak valid." }); return; }
    if (!adjReason().trim()) { setMsg({ ok: false, t: "Alasan penyesuaian wajib diisi." }); return; }
    setBusy(true);
    try {
      await api.send("/api/stock/adjust", "POST", { branch: r.branch, item: r.code, gd: r.gd, qty: +adjQty(), reason: adjReason().trim() });
      setAdjRow(null);
      setMsg({ ok: true, t: "Penyesuaian dikirim ke " + r.branch + " — stok " + r.code + " di " + r.gd + " → " + fmt(+adjQty()) + ". Berlaku sesaat lagi." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };

  return h("div", { class: "main" }, Topbar({ title: "Posisi Stok", sub: isAdminRole() ? "Stok terkini dari seluruh outlet" : "Stok terkini di outlet Anda" }),
    h("div", { class: "content" }, [
      () => low().length
        ? h("div", { class: "card", style: "margin-bottom:18px;border-color:#EFCcc7;background:#FBF0EE" }, [
            h("h3", { style: "color:#8a2f27" }, "⚠ Bahan Baku Menipis (" + low().length + ")"),
            h("div", { class: "hint" }, "Item di bawah batas minimum stok."),
            h("div", { style: "margin:2px 0 10px" }, xlsxBtn(() => "BahanBakuMenipis-" + dstamp(),
              ["Kode", "Nama", "Outlet", "Gudang", "Stok", "Min"],
              () => low().map((x: any) => [x.code, x.name, x.branch, x.gd, +x.qty || 0, +x.min || 0]))),
            h("table", { class: "table" },
              h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Outlet"), h("th", null, "Gudang"),
                h("th", { style: "text-align:right" }, "Stok"), h("th", { style: "text-align:right" }, "Min"))),
              h("tbody", null, () => low().map((x) =>
                h("tr", null,
                  h("td", null, h("b", null, x.code), " ", x.name),
                  h("td", null, x.branch), h("td", null, x.gd),
                  h("td", { style: "text-align:right" }, h("b", { style: "color:#b5453b" }, fmt(x.qty))),
                  h("td", { style: "text-align:right" }, fmt(x.min)))))),
          ])
        : null,

      h("div", { class: "card" }, [
        h("div", { class: "row", style: "gap:10px;flex-wrap:wrap" }, [
          () => isAdminRole() ? h("select", { class: "input", style: "max-width:180px",
            onChange: (e: any) => { setBranch(e.currentTarget.value); load(); } },
            h("option", { value: "" }, "Semua Outlet"),
            branches().map((b) => h("option", { value: b.code }, b.name))) : null,
          h("select", { class: "input", style: "max-width:160px", onChange: (e: any) => { setJenis(e.currentTarget.value); load(); } }, jenisOptions(jenis)),
          h("select", { class: "input", style: "max-width:150px", onChange: (e: any) => { setGd(e.currentTarget.value); load(); } },
            h("option", { value: "" }, "Semua Gudang"),
            () => gdOpts().map((g) => h("option", { value: g, selected: () => gd() === g }, g))),
          h("input", { class: "input", placeholder: "Cari kode / nama...", style: "flex:1;min-width:170px",
            value: q, onInput: (e: any) => setQ(e.currentTarget.value),
            onKeyDown: (e: any) => { if (e.key === "Enter") load(); } }),
          h("button", { class: "btn btn-primary", onClick: load }, "Cari"),
          () => canAdjust() ? h("button", { class: "btn", disabled: syncing, title: "Tarik stok terbaru dari semua outlet sekarang (tanpa menunggu interval)", onClick: syncNow },
            () => syncing() ? h("span", { class: "spin" }) : null, "⟳ Sync Now") : null,
          xlsxBtn(() => "PosisiStok-" + dstamp(), ["Kode", "Nama", "Jenis", "Satuan", "Outlet", "Gudang", "Stok"],
            () => rows().map((r: any) => [r.code, r.name, jenisText(r.jenis), r.unit || "", r.branch, r.gd, +r.qty || 0])),
        ]),
        () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err"), style: "margin-top:12px" }, msg().t) : null,
        h("div", { class: "hint", style: "margin:12px 0 0" },
          () => loading() ? "Memuat…" : rows().length + " baris ditampilkan (maks 400)"),
      ]),

      // Adjust panel — appears when a row's "Sesuaikan" is clicked.
      () => adjRow() ? h("div", { class: "card", style: "margin-top:18px;border-color:var(--brand-soft);background:#FFF8EF" }, [
        h("h3", { style: "margin:0 0 4px" }, "Sesuaikan Stok Fisik"),
        h("div", { class: "hint" }, h("b", null, adjRow().code), " ", adjRow().name,
          " · outlet " + adjRow().branch + " · gudang " + adjRow().gd + " · stok sistem " + fmt(adjRow().qty)),
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
          field("Stok Fisik (hasil hitung)", h("input", { class: "input", type: "number", value: adjQty, onInput: (e: any) => setAdjQty(e.currentTarget.value) })),
          field("Alasan", h("input", { class: "input", placeholder: "mis. opname fisik, koreksi salah input…", value: adjReason, onInput: (e: any) => setAdjReason(e.currentTarget.value) })),
          h("button", { class: "btn btn-primary", disabled: busy, onClick: saveAdjust }, () => busy() ? h("span", { class: "spin" }) : null, "Simpan Penyesuaian"),
          h("button", { class: "btn btn-ghost", onClick: () => setAdjRow(null) }, "Batal"),
        ]),
      ]) : null,

      h("div", { class: "card", style: "margin-top:18px;padding:6px 0 0;overflow-x:auto" },
        h("table", { class: "table" },
          h("thead", null, h("tr", null,
            h("th", { style: "padding-left:22px" }, "Kode"), h("th", null, "Nama"), h("th", null, "Jenis"),
            h("th", null, "Satuan"), h("th", null, "Outlet"), h("th", null, "Gudang"), h("th", { style: "text-align:right" }, "Stok"),
            () => canAdjust() ? h("th", { style: "text-align:right;padding-right:22px" }, "") : null)),
          h("tbody", null, () => rows().length
            ? rows().map((r) => h("tr", null,
                h("td", { style: "padding-left:22px" }, h("b", null, r.code)),
                h("td", null, r.name),
                h("td", null, jenisBadge(r.jenis)),
                h("td", null, r.unit || h("span", { class: "muted" }, "—")),
                h("td", null, r.branch),
                h("td", null, r.gd),
                h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" }, fmt(r.qty)),
                () => canAdjust() ? h("td", { style: "text-align:right;padding-right:22px" },
                  h("button", { class: "btn btn-ghost", style: "padding:4px 10px;font-size:12px", onClick: () => openAdjust(r) }, "Sesuaikan")) : null))
            : h("tr", null, h("td", { colspan: canAdjust() ? 8 : 7, class: "muted", style: "text-align:center;padding:34px" },
                loading() ? "Memuat…" : "Belum ada data. Jalankan agent untuk sinkronisasi."))))),
    ]));
}

// Role meanings (client model 2026-07): pusat_production = the PRODUCTION-LINE
// user (sees only production menus); cabang_user = "Petugas Outlet" — outlet
// staff at ANY outlet, including Pusat's own outlet (outlets are equal peers).
const ROLE_LABELS: any = { owner: "Owner", admin: "Admin", pusat_production: "Produksi", cabang_user: "Petugas Outlet" };
const roleLabel = (r: string) => ROLE_LABELS[r] || "—";
const field = (label: string, input: any) =>
  h("div", { class: "field", style: "margin-bottom:0;min-width:140px;flex:1" }, h("label", null, label), input);

function UsersPage() {
  const [list, setList] = createSignal<any[]>([]);
  const [branches, setBranches] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [uName, setUName] = createSignal("");
  const [uPass, setUPass] = createSignal("");
  const [uFull, setUFull] = createSignal("");
  const [uRole, setURole] = createSignal("cabang_user");
  const [uBranch, setUBranch] = createSignal("");

  const load = async () => { try { setList(await api.get("/api/users")); } catch (_) {} };
  onMount(async () => { await load(); try { setBranches(await api.get("/api/branches")); } catch (_) {} });

  const create = async () => {
    setMsg(null);
    try {
      await api.send("/api/users", "POST", { username: uName(), password: uPass(), full_name: uFull(), role: uRole(), branch: uBranch() });
      setUName(""); setUPass(""); setUFull(""); setURole("cabang_user"); setUBranch("");
      await load(); setMsg({ ok: true, t: "Pengguna dibuat." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const toggle = async (u: any) => {
    await api.send("/api/users/update", "POST", { id: u.id, full_name: u.full_name, role: u.role, branch: u.branch, active: u.active ? 0 : 1 });
    await load();
  };
  const resetPw = async (u: any) => {
    const p = prompt("Kata sandi baru untuk " + u.username + " (min. 6 karakter):");
    if (!p) return;
    try { await api.send("/api/users/reset-password", "POST", { id: u.id, password: p }); setMsg({ ok: true, t: "Kata sandi direset." }); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };

  return h("div", { class: "main" }, Topbar({ title: "Pengguna", sub: "Kelola akun & peran" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      h("div", { class: "card" }, [
        h("h3", null, "Tambah Pengguna"),
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end" }, [
          field("Nama Pengguna", h("input", { class: "input", value: uName, onInput: (e: any) => setUName(e.currentTarget.value) })),
          field("Kata Sandi", h("input", { class: "input", type: "password", value: uPass, onInput: (e: any) => setUPass(e.currentTarget.value) })),
          field("Nama Lengkap", h("input", { class: "input", value: uFull, onInput: (e: any) => setUFull(e.currentTarget.value) })),
          field("Peran", h("select", { class: "input", value: uRole, onChange: (e: any) => setURole(e.currentTarget.value) },
            h("option", { value: "cabang_user" }, "Petugas Outlet"),
            h("option", { value: "pusat_production" }, "Produksi"),
            h("option", { value: "admin" }, "Admin"),
            // Only an owner may grant owner (backend enforces this too).
            () => isOwnerRole() ? h("option", { value: "owner" }, "Owner") : null)),
          field("Outlet", h("select", { class: "input", value: uBranch, onChange: (e: any) => setUBranch(e.currentTarget.value) },
            h("option", { value: "" }, "—"),
            () => branches().map((b) => h("option", { value: b.code }, b.name)))),
          h("button", { class: "btn btn-primary", onClick: create }, "Tambah"),
        ]),
      ]),
      h("div", { class: "card", style: "margin-top:18px;padding:6px 0 0" },
        h("table", { class: "table" },
          h("thead", null, h("tr", null,
            h("th", { style: "padding-left:22px" }, "Pengguna"), h("th", null, "Nama"), h("th", null, "Peran"),
            h("th", null, "Outlet"), h("th", null, "Status"), h("th", { style: "text-align:right;padding-right:22px" }, "Aksi"))),
          h("tbody", null, () => list().map((u) =>
            h("tr", null,
              h("td", { style: "padding-left:22px" }, h("b", null, u.username)),
              h("td", null, u.full_name || "—"),
              h("td", null, h("span", { class: "badge pusat" }, roleLabel(u.role))),
              h("td", null, u.branch || "—"),
              h("td", null, u.active
                ? h("span", { class: "badge cabang" }, "Aktif")
                : h("span", { class: "badge", style: "background:#eee;color:#999" }, "Nonaktif")),
              h("td", { style: "text-align:right;padding-right:22px;white-space:nowrap" },
                // An owner account can only be managed by an owner — hide the
                // actions for everyone else (the backend refuses them anyway).
                (u.role === "owner" && !isOwnerRole())
                  ? h("span", { class: "muted", style: "font-size:12px" }, "—")
                  : [
                      h("button", { class: "btn btn-ghost", style: "padding:5px 10px;font-size:12px", onClick: () => resetPw(u) }, "Reset PW"),
                      " ",
                      h("button", { class: "btn btn-ghost", style: "padding:5px 10px;font-size:12px", onClick: () => toggle(u) },
                        u.active ? "Nonaktifkan" : "Aktifkan"),
                    ])))))),
    ]));
}

const statusBadge = (s: string) => {
  const map: any = {
    submitted: ["Diajukan", "pusat"], produced: ["Diproduksi", "cabang"], closed: ["Selesai", "cabang"],
    sent: ["Dikirim", "pusat"], received: ["Diterima", "cabang"], draft: ["Draft", "pusat"], partial: ["Sebagian", "pusat"],
    open: ["Terbuka", "pusat"], cancelled: ["Dibatalkan", "cabang"],
  };
  const m = map[s] || [s, "pusat"];
  return h("span", { class: "badge " + m[1] }, m[0]);
};

function PermintaanPage() {
  const [list, setList] = createSignal<any[]>([]);
  const [sug, setSug] = createSignal<any[]>([]);
  // The suggestion list IS the permintaan (client rule): there is no separate
  // "add to draft" step. The kasir only removes what they don't want, so the
  // state tracked is what was REMOVED — that way a live stock refresh updates
  // quantities without resurrecting items the kasir already took out.
  const [removed, setRemoved] = createSignal<string[]>([]);
  const draft = createMemo(() => sug().filter((s: any) => !removed().includes(s.item)));
  const [note, setNote] = createSignal("");
  const [msg, setMsg] = createSignal<any>(null);
  const [reqBranch, setReqBranch] = createSignal("");
  const [branchOpts, setBranchOpts] = createSignal<any[]>([]);
  const [flBranch, setFlBranch] = createSignal("");
  const [flStatus, setFlStatus] = createSignal("");
  const [flQ, setFlQ] = createSignal("");
  const isCabang = canOutlet;
  const isPusat = canProduce;
  const isAdmin = isAdminRole;

  const load = async () => {
    const p = new URLSearchParams();
    if (isAdmin() && flBranch()) p.set("branch", flBranch());
    if (flStatus()) p.set("status", flStatus());
    if (flQ()) p.set("q", flQ());
    try { setList(await api.get("/api/requests?" + p.toString())); } catch (_) {}
  };
  const [sugHorizon, setSugHorizon] = createSignal(3);
  const loadSug = async () => {
    if (!isCabang()) return;
    try {
      const url = "/api/requests/suggestions" + (isAdmin() && reqBranch() ? "?branch=" + encodeURIComponent(reqBranch()) : "");
      const r = await api.get(url); setSug(r?.suggestions || []); setSugHorizon(r?.horizon_days ?? 3);
    } catch (_) {}
  };
  onMount(async () => {
    if (isAdmin()) {
      try {
        const bs = (await api.get("/api/branches") || []).filter((b: any) => b.type === "cabang");
        setBranchOpts(bs); if (bs[0]) setReqBranch(bs[0].code);
      } catch (_) {}
    }
    await load(); await loadSug();
  });
  onLive(["requests"], load);
  onLive(["stock"], loadSug);

  // Quantities are never sent — only item codes; the server recomputes par − stok.
  const submit = async () => {
    if (!draft().length) return;
    setMsg(null);
    try {
      const r = await api.send("/api/requests", "POST", { items: draft().map((d: any) => d.item), note: note(), branch: reqBranch() });
      setRemoved([]); setNote(""); await load(); await loadSug();
      setMsg({ ok: true, t: "Permintaan terkirim." + (r?.skipped?.length ? " (" + r.skipped.length + " item dilewati — sudah ada permintaan terbuka atau stok sudah di atas batas: " + r.skipped.join(", ") + ")" : "") });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  /* ---- tarik permintaan: cabang asks, pusat answers ----
     Both sides act on the SAME row rather than on a separate queue screen: the
     ask is only meaningful next to the item it withdraws, and pusat deciding it
     is a judgement about goods it can see listed right there. `items` empty
     means the whole document, which is exactly what the server takes. */
  // Only an APPROVED withdrawal frees the item to be requested again, so only
  // that case is worth refetching the (expensive) suggestion list for.
  const post = async (url: string, body: any, okText: string, resuggest?: boolean) => {
    setMsg(null);
    try {
      await api.send(url, "POST", body);
      await load();
      if (resuggest) await loadSug();
      setMsg({ ok: true, t: okText });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const ask = (req: any, items: string[]) =>
    post("/api/requests/retract", { request_id: req.id, items }, "Permintaan tarik dikirim — menunggu persetujuan pusat.");
  const decide = (req: any, items: string[], approve: boolean) =>
    post("/api/requests/retract/decide", { request_id: req.id, items, approve },
      approve ? "Penarikan disetujui — item dikeluarkan dari permintaan." : "Penarikan ditolak.", approve);

  // Withdrawable only until the goods leave: `dispatched` is the SJ-has-departed
  // flag, NOT `claimed` (a draft claim, where retraction is still fine).
  //
  // Deliberately coarser than the server, which refuses per ITEM: once ANY line
  // of a request ships, short-shipping forfeits the rest (see sj.go's header),
  // so the lines this hides are dead demand that will resurface tomorrow anyway.
  const canAsk = (req: any) => isCabang() && req.status === "submitted" && !req.dispatched;
  const nLines = (req: any, st: string) => (req.lines || []).filter((l: any) => l.retract === st).length;

  const miniBtn = (label: string, onClick: any, tone?: string) =>
    h("button", { class: "btn btn-ghost", style: "padding:1px 7px;font-size:11px;margin-left:6px" + (tone || ""), onClick }, label);

  const retractLine = (req: any) => (l: any) => {
    const st = l.retract;
    const skin = st === "done" ? ";text-decoration:line-through;opacity:.6"
      : st === "pending" ? ";background:#FBEECB;color:#A9781F" : "";
    return lineBadge(l, skin, [
      st === "done" ? " · ditarik" : null,
      st === "pending" ? " · minta ditarik" + (l.retract_at ? " " + l.retract_at : "") : null,
      st === "none" && canAsk(req) ? miniBtn("Tarik", () => ask(req, [l.item])) : null,
      st === "pending" && isPusat() ? [
        miniBtn("Setujui", () => decide(req, [l.item], true), ";color:#b5453b"),
        miniBtn("Tolak", () => decide(req, [l.item], false)),
      ] : null,
    ]);
  };

  // Document-level actions. Kept off the line row because "tarik semua" is a
  // different decision from withdrawing one item, and pusat answering a whole
  // document at once is the common case when a cabang changes its mind.
  const retractBar = (req: any) => {
    const pend = nLines(req, "pending");
    if (isPusat() && pend)
      return h("div", { class: "hint", style: "margin-top:8px;color:#A9781F" },
        "Cabang minta menarik " + pend + " item. ",
        miniBtn("Setujui semua", () => decide(req, [], true), ";color:#b5453b"),
        miniBtn("Tolak semua", () => decide(req, [], false)));
    if (canAsk(req) && nLines(req, "none") > 1)
      return miniBtn("Tarik seluruh permintaan", () => ask(req, []));
    return null;
  };

  // Aggregate model: production is no longer per-request — the production line
  // works from the Rencana Produksi board on the Produksi page, and a request
  // is fulfilled by its Surat Jalan (complete-only), created on the SJ page.
  return h("div", { class: "main" }, Topbar({ title: "Permintaan Kebutuhan Display",
    sub: "Kebutuhan display dari outlet (jumlah terkunci " + minParLabel() + ")" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      () => isAdmin() ? h("div", { class: "card", style: "margin-bottom:18px;display:flex;align-items:center;gap:12px;flex-wrap:wrap" }, [
        h("b", null, "Untuk Outlet:"),
        h("select", { class: "input", style: "max-width:240px", value: reqBranch,
          onChange: (e: any) => { setReqBranch(e.currentTarget.value); loadSug(); } },
          () => branchOpts().map((b: any) => h("option", { value: b.code }, b.name))),
      ]) : null,
      () => isCabang() ? h("div", { class: "card" }, [
        h("h3", null, () => "Permintaan Hari Ini (" + draft().length + " item)"),
        h("div", { class: "hint" }, () => "Semua kekurangan display (" + parLabel() + " − stok) + kebutuhan pesanan pelanggan yang diambil ≤ " +
          sugHorizon() + " hari ke depan sudah masuk daftar. Keluarkan yang tidak perlu, lalu tekan Kirim Permintaan. Jumlah terkunci — untuk mengubahnya, ubah angkanya di Batas Stok (tercatat). " +
          "Item yang sudah diminta dan barangnya belum datang tidak ditampilkan lagi, supaya tidak terpesan dua kali."),
        () => removed().length
          ? h("div", { class: "hint", style: "margin-top:6px;color:" + SEMI_INK },
              removed().length + " item dikeluarkan. ",
              h("button", { class: "btn btn-ghost", style: "padding:2px 9px;font-size:12px", onClick: () => setRemoved([]) }, "Kembalikan semua"))
          : null,
        () => draft().length ? h("table", { class: "table", style: "margin-top:12px" },
          h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama"),
            h("th", { style: "text-align:right" }, "Stok"), minTh(),
            parTh(), h("th", { style: "text-align:right" }, "Utk Pesanan"),
            h("th", { style: "text-align:right" }, "Jumlah"), h("th", null, ""))),
          h("tbody", null, () => draft().map((d: any) => h("tr", null,
            h("td", null, h("b", null, d.item)),
            h("td", null, d.name || h("span", { class: "muted" }, "—")),
            h("td", { style: "text-align:right" }, fmt(d.current)),
            minTd(d.min),
            h("td", { style: "text-align:right" }, fmt(d.par)),
            h("td", { style: "text-align:right" }, d.pesanan > 0 ? h("b", { style: "color:var(--gold-strong,#9a7b2f)" }, fmt(d.pesanan)) : h("span", { class: "muted" }, "—")),
            numTd(d.suggested, ";font-weight:700;color:var(--brand-strong)"),
            h("td", { style: "text-align:right" }, h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => setRemoved([...removed(), d.item]) }, "Keluarkan"))))))
          : h("div", { class: "muted", style: "margin-top:10px" },
              "Belum ada yang perlu diminta — stok display semua item masih di atas batas, atau permintaannya sudah dikirim dan barangnya belum datang."),
        h("div", { class: "field", style: "margin-top:12px" }, h("label", null, "Catatan"),
          h("input", { class: "input", value: note, onInput: (e: any) => setNote(e.currentTarget.value) })),
        h("button", { class: "btn btn-primary", onClick: submit, disabled: () => !draft().length }, "Kirim Permintaan"),
      ]) : null,
      h("div", { class: "card", style: "margin-top:18px" }, [
        h("div", { class: "row between", style: "align-items:center" },
          h("h3", { style: "margin:0" }, "Daftar Permintaan"),
          xlsxBtn(() => "Permintaan-" + dstamp(),
            ["No Permintaan", "Status", "Outlet", "Tgl", "Kode", "Jumlah", "Catatan"],
            () => (list() || []).flatMap((req: any) => (req.lines || []).map((l: any) => [req.request_no, req.status, req.branch_name, req.when, l.item, +l.qty || 0, req.note || ""])))),
        h("div", { class: "filters", style: "margin:12px 0 14px" }, [
          () => isAdmin() ? h("select", { class: "input", onChange: (e: any) => { setFlBranch(e.currentTarget.value); load(); } },
            h("option", { value: "" }, "Semua Outlet"), () => branchOpts().map((b: any) => h("option", { value: b.code }, b.name))) : null,
          h("select", { class: "input", onChange: (e: any) => { setFlStatus(e.currentTarget.value); load(); } },
            h("option", { value: "" }, "Semua Status"),
            h("option", { value: "terbuka" }, "Terbuka (belum ada SJ)"),
            h("option", { value: "dikirim" }, "Sudah Dikirim")),
          h("input", { class: "input grow", placeholder: "Cari No. Permintaan…", value: flQ,
            onInput: (e: any) => { setFlQ(e.currentTarget.value); load(); } }),
        ]),
        h("div", { class: "grid", style: "gap:12px" },
          () => list().length ? list().map((req) =>
            h("div", { style: "border:1px solid var(--line);border-radius:11px;padding:14px 16px" }, [
              h("div", { class: "row between" }, [
                // "Dikirim" means the goods LEFT, not that a draft reserved them:
                // a merely-claimed request still offers Tarik, and a card cannot
                // say the shipment went while inviting the cabang to call it back.
                h("div", null, h("b", null, req.request_no), " ",
                  req.dispatched ? statusBadge("sent")
                    : req.claimed ? h("span", { class: "badge", style: "background:#FBEECB;color:#A9781F" }, "Disiapkan")
                    : statusBadge(req.status),
                  h("div", { class: "muted", style: "font-size:12.5px;margin-top:3px" }, req.branch_name + " · " + req.when)),
                () => (isPusat() && req.status === "submitted")
                  ? h("a", { class: "btn btn-ghost", style: "padding:6px 12px;font-size:12.5px", href: "#/suratjalan" }, "Buat SJ →") : null,
              ]),
              h("div", { style: "margin-top:9px" }, () => req.lines.map(retractLine(req))),
              retractBar(req),
              req.note ? h("div", { class: "muted", style: "font-size:12.5px;margin-top:7px" }, "Catatan: " + req.note) : null,
            ]))
            : h("div", { class: "muted" }, "Belum ada permintaan.")),
      ]),
    ]));
}

function TerimaPage() {
  const [list, setList] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [gd, setGd] = createSignal<any>({});
  const [q, setQ] = createSignal("");
  const load = async () => { try { setList(await api.get("/api/deliveries" + (q() ? "?q=" + encodeURIComponent(q()) : ""))); } catch (_) {} };
  onMount(load);
  onLive(["deliveries"], load);
  const receive = async (d: any) => {
    setMsg(null);
    const target = gd()[d.id] || "GD";
    try { const r = await api.send("/api/deliveries/receive", "POST", { delivery_id: d.id, target_gd: target }); await load(); setMsg({ ok: true, t: "Diterima ke " + r.target_gd + " (SJ " + r.sj_no + ")" }); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  return h("div", { class: "main" }, Topbar({ title: "Terima Produksi", sub: "Terima Surat Jalan dari pusat ke stok outlet" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      h("div", { class: "card", style: "margin-bottom:14px" },
        h("input", { class: "input", placeholder: "Cari No. Surat Jalan…",
          value: q, onInput: (e: any) => { setQ(e.currentTarget.value); load(); } })),
      h("div", { class: "grid", style: "gap:14px" },
        () => list().length ? list().map((d) =>
          h("div", { class: "card" }, [
            h("div", { class: "row between" }, [
              h("div", null, h("b", null, d.sj_no), " ", statusBadge(d.status),
                h("div", { class: "muted", style: "font-size:12.5px;margin-top:3px" }, "Dari " + d.from + " · " + d.when)),
              () => d.unreceived > 0 ? h("div", { class: "row", style: "gap:8px" }, [
                h("select", { class: "input", style: "width:auto;padding:8px 10px", onChange: (e: any) => setGd({ ...gd(), [d.id]: e.currentTarget.value }) },
                  h("option", { value: "GD" }, "GD (Outlet)"), h("option", { value: "GD1" }, "GD1 (Gudang Bawah)")),
                h("button", { class: "btn btn-primary", style: "padding:8px 14px", onClick: () => receive(d) }, "Terima"),
              ]) : h("span", { class: "badge cabang" }, "Selesai"),
            ]),
            h("table", { class: "table", style: "margin-top:10px" },
              h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama"), h("th", { style: "text-align:right" }, "Jumlah"))),
              h("tbody", null, () => d.lines.map((l: any) =>
                h("tr", null, h("td", null, h("b", null, l.item)), h("td", null, l.name), h("td", { style: "text-align:right" }, fmt(l.qty)))))),
          ]))
          : h("div", { class: "muted" }, "Belum ada kiriman.")),
    ]));
}

function RecipesPage() {
  const [rows, setRows] = createSignal<any[]>([]);
  const [q, setQ] = createSignal("");
  const [type, setType] = createSignal("");
  const [loading, setLoading] = createSignal(false);
  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q()) p.set("q", q());
    if (type()) p.set("type", type());
    try { setRows(await api.get("/api/recipes?" + p.toString()) || []); } catch (_) {}
    setLoading(false);
  };
  onMount(load);
  onLive(["recipes"], load);

  return h("div", { class: "main" }, Topbar({ title: "Resep Produk", sub: "Bill of Material dari sistem produksi" }),
    h("div", { class: "content" }, [
      h("div", { class: "card" }, [
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap" }, [
          h("select", { class: "input", style: "max-width:190px",
            onChange: (e: any) => { setType(e.currentTarget.value); load(); } },
            h("option", { value: "" }, "Semua Jenis"),
            h("option", { value: "finished" }, "Barang Jadi"),
            h("option", { value: "semi" }, "Setengah Jadi")),
          h("input", { class: "input", placeholder: "Cari kode atau nama produk...", style: "flex:1;min-width:200px",
            value: q, onInput: (e: any) => setQ(e.currentTarget.value),
            onKeyDown: (e: any) => { if (e.key === "Enter") load(); } }),
          h("button", { class: "btn btn-primary", onClick: load }, "Cari"),
        ]),
        h("div", { class: "hint", style: "margin:12px 0 0" },
          () => loading() ? "Memuat…" : rows().length + " resep (maks 300)"),
      ]),
      h("div", { class: "grid", style: "gap:14px;margin-top:18px" },
        () => rows().length ? rows().map((r) =>
          h("div", { class: "card" }, [
            h("div", { class: "row between", style: "align-items:flex-start" }, [
              h("div", null,
                h("div", null, h("b", { style: "font-size:15px" }, r.name || r.output_item),
                  " ", jenisBadge(r.output_type)),
                h("div", { class: "muted", style: "font-size:12.5px;margin-top:3px" },
                  "Kode " + r.output_item + " · resep " + r.recipe_code + " · hasil " + fmt(r.yield_qty))),
            ]),
            r.lines && r.lines.length
              ? h("table", { class: "table", style: "margin-top:10px" },
                  h("thead", null, h("tr", null, h("th", null, "Komponen"), h("th", null, "Nama"),
                    h("th", { style: "text-align:right" }, "Jumlah"))),
                  h("tbody", null, r.lines.map((l: any) =>
                    h("tr", null, h("td", null, h("b", null, l.item)), h("td", null, l.name || "—"),
                      h("td", { style: "text-align:right" }, fmt(l.qty))))))
              : h("div", { class: "muted", style: "margin-top:8px;font-size:12.5px" }, "Tidak ada komponen."),
          ]))
          : h("div", { class: "muted" }, loading() ? "Memuat…" : "Belum ada resep. Jalankan agent untuk sinkronisasi BOM.")),
    ]));
}

function BranchesPage() {
  const [list, setList] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [code, setCode] = createSignal("");
  const [name, setName] = createSignal("");
  const [type, setType] = createSignal("cabang");
  const [ldb, setLdb] = createSignal("");
  const load = async () => { try { setList(await api.get("/api/branches/manage")); } catch (_) {} };
  onMount(load);
  onLive(["branches", "stock"], load);

  const create = async () => {
    setMsg(null);
    try {
      await api.send("/api/branches", "POST", { code: code(), name: name(), type: type(), local_db: ldb() });
      setCode(""); setName(""); setLdb(""); setType("cabang");
      await load(); setMsg({ ok: true, t: "Outlet ditambahkan. Deploy agent dengan BRANCH_CODE ini agar mulai sinkron." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const toggle = async (b: any) => {
    setMsg(null);
    try { await api.send("/api/branches/update", "POST", { id: b.id, name: b.name, local_db: b.local_db, active: b.active ? 0 : 1 }); await load(); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const rename = async (b: any) => {
    const nm = prompt("Nama baru untuk " + b.code + ":", b.name);
    if (!nm || nm === b.name) return;
    setMsg(null);
    try { await api.send("/api/branches/update", "POST", { id: b.id, name: nm, local_db: b.local_db, active: b.active }); await load(); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };

  return h("div", { class: "main" }, Topbar({ title: "Outlet", sub: "Kelola Outlet Pusat & Cabang" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      h("div", { class: "card" }, [
        h("h3", null, "Tambah Outlet"),
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end" }, [
          field("Kode", h("input", { class: "input", value: code, placeholder: "mis. CB3",
            onInput: (e: any) => setCode(e.currentTarget.value.toUpperCase()) })),
          field("Nama", h("input", { class: "input", value: name, placeholder: "mis. Cabang 3",
            onInput: (e: any) => setName(e.currentTarget.value) })),
          field("Jenis", h("select", { class: "input", value: type, onChange: (e: any) => setType(e.currentTarget.value) },
            h("option", { value: "cabang" }, "Cabang"),
            h("option", { value: "pusat" }, "Pusat"))),
          h("button", { class: "btn btn-primary", onClick: create }, "Tambah"),
        ]),
      ]),
      h("div", { class: "card", style: "margin-top:18px;padding:6px 0 0;overflow-x:auto" },
        h("table", { class: "table" },
          h("thead", null, h("tr", null,
            h("th", { style: "padding-left:22px" }, "Kode"), h("th", null, "Nama"), h("th", null, "Jenis"),
            h("th", { style: "text-align:right" }, "Pengguna"), h("th", null, "Sinkron Terakhir"),
            h("th", null, "Status"), h("th", { style: "text-align:right;padding-right:22px" }, "Aksi"))),
          h("tbody", null, () => list().length ? list().map((b) =>
            h("tr", null,
              h("td", { style: "padding-left:22px" }, h("b", null, b.code)),
              h("td", null, b.name),
              h("td", null, h("span", { class: "badge " + b.type }, b.type === "pusat" ? "Pusat" : "Cabang")),
              h("td", { style: "text-align:right" }, String(b.users)),
              h("td", null, b.last_sync
                ? h("span", { class: "muted", style: "font-size:12.5px" }, b.last_sync)
                : h("span", { class: "muted", style: "font-size:12.5px" }, "— belum ada")),
              h("td", null, b.active
                ? h("span", { class: "badge cabang" }, "Aktif")
                : h("span", { class: "badge", style: "background:#eee;color:#999" }, "Nonaktif")),
              h("td", { style: "text-align:right;padding-right:22px;white-space:nowrap" },
                h("button", { class: "btn btn-ghost", style: "padding:5px 10px;font-size:12px", onClick: () => rename(b) }, "Ubah Nama"),
                " ",
                h("button", { class: "btn btn-ghost", style: "padding:5px 10px;font-size:12px", onClick: () => toggle(b) },
                  b.active ? "Nonaktifkan" : "Aktifkan"))))
            : h("tr", null, h("td", { colspan: 7, class: "muted", style: "text-align:center;padding:34px" }, "Belum ada outlet."))))),
    ]));
}

function ThresholdsPage() {
  const [branches, setBranches] = createSignal<any[]>([]);
  onMount(async () => { if (isAdminRole()) { try { setBranches(await api.get("/api/branches")); } catch (_) {} } });
  return h("div", { class: "main" }, Topbar({ title: "Batas Stok", sub: "Ambang minimum & par untuk peringatan dan saran otomatis" }),
    h("div", { class: "content" }, [
      () => canOutlet() ? OutletMinSection({ isAdmin: isAdminRole, branches }) : null,
      () => canProduce() ? h("div", { style: canOutlet() ? "margin-top:18px" : "" }, BBThreshSection({ isAdmin: isAdminRole, branches })) : null,
    ]));
}

function OutletMinSection(p: { isAdmin: () => boolean; branches: () => any[] }) {
  const [list, setList] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [branch, setBranch] = createSignal("");
  const [item, setItem] = createSignal("");
  const [min, setMin] = createSignal("");
  const [par, setPar] = createSignal("");
  const load = async () => { try { setList(await api.get("/api/outlet-minimums")); } catch (_) {} };
  onMount(load);
  onLive(["stock"], load);
  const reset = () => { setItem(""); setMin(""); setPar(""); if (p.isAdmin()) setBranch(""); };
  const save = async () => {
    setMsg(null);
    try {
      // Omit min entirely while the switch is on: the server then keeps the
      // stored value instead of overwriting it, so turning the switch back off
      // restores the old thresholds.
      const body: any = { branch: branch(), item: item(), par: +par() || 0 };
      if (!minEqPar()) body.min = +min() || 0;
      await api.send("/api/outlet-minimums", "POST", body);
      reset(); await load(); if (showLog()) loadLog(); setMsg({ ok: true, t: "Tersimpan." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const edit = (r: any) => { if (p.isAdmin()) setBranch(r.branch); setItem(r.item); setMin(String(r.min)); setPar(String(r.par)); };
  const del = async (r: any) => {
    try { await api.send("/api/outlet-minimums/delete", "POST", { branch: r.branch, item: r.item }); await load(); if (showLog()) loadLog(); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  // Riwayat: the audit trail of min/par changes (quantities in permintaan are
  // locked to these, so this log is the "who changed what" record).
  const [showLog, setShowLog] = createSignal(false);
  const [log, setLog] = createSignal<any[]>([]);
  const loadLog = async () => { try { setLog(await api.get("/api/outlet-minimums/log") || []); } catch (_) {} };
  const toggleLog = async () => { const v = !showLog(); setShowLog(v); if (v) await loadLog(); };
  const delta = (from: any, to: any) => (from != null ? fmt(from) + " → " : "") + fmt(to);
  // Under "Minimal = Standar" the Min is frozen and hidden on every other screen,
  // so reporting it here would describe a field the operator cannot see or set.
  // But this is HISTORY and a display flag must not redact it: before the switch
  // was thrown, editing Min alone was ordinary, and hiding it wholesale would
  // render such a row as an unchanged "Standar 10 → 10".
  //
  // So Min is hidden only where the row itself proves it never moved. Each shape
  // logs different evidence, hence three cases — which must NOT be folded
  // together: a delete logs new_min NULL, so testing that would both mis-hide the
  // row and hand fmt() a null.
  //   delete   — no new_* logged; a frozen min equals its own par
  //   creation — no old_*; the server seeds new_min from par while locked
  //   edit     — the plain "did it move" comparison
  const hideMin = (r: any) => minEqPar() && (
    r.action === "delete" ? +r.old_min === +r.old_par
      : r.old_min == null ? +r.new_min === +r.new_par
        : +r.old_min === +r.new_min);
  const chg = (r: any) => r.action === "delete"
    ? "dihapus (" + (hideMin(r) ? "" : "min " + fmt(r.old_min) + ", ") + parLabel() + " " + fmt(r.old_par) + ")"
    : (r.old_min == null ? "baru: " : "")
      + (hideMin(r) ? "" : "min " + delta(r.old_min, r.new_min) + " · ")
      + parLabel() + " " + delta(r.old_par, r.new_par);
  // "Minta jadi ke Pusat" per finishable item — only while the global setting
  // is ON (OFF hides the column; every item then follows mandatory finishing).
  const showJadi = () => settings().allow_cabang_order_finished === true;
  const setJadi = async (r: any, finished: boolean) => {
    setMsg(null);
    // success needs no reload — the "stock" broadcast refreshes the list;
    // on error reload to snap the checkbox back.
    try { await api.send("/api/cabang-item-mode", "POST", { branch: r.branch, item: r.item, finished }); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); await load(); }
  };
  // Tracks the header exactly: minTh() drops a column while "Minimal = Standar"
  // is on, so the empty-state row has to lose one too.
  const span = () => (p.isAdmin() ? 7 : 6) + (showJadi() ? 1 : 0) - (minEqPar() ? 1 : 0);
  return h("div", { class: "card" }, [
    h("h3", null, minEqPar() ? "Standar Display Outlet" : "Batas Minimum Outlet (Min / Par)"),
    h("div", { class: "hint" }, "Barang jadi di rak outlet (GD). " +
      (minEqPar()
        ? "Minimal disamakan dengan Standar (Pengaturan), jadi setiap kekurangan langsung memunculkan saran"
        : "Saran otomatis muncul saat stok ≤ Min") +
      " sebesar " + parLabel() + " − stok. Untuk Pusat, " + parLabel() +
      " juga menjadi Stndrt G. Bawah — target buffer produksi pagi di papan Rencana Produksi."),
    () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err"), style: "margin-top:10px" }, msg().t) : null,
    h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
      () => p.isAdmin() ? field("Outlet", h("select", { class: "input", value: branch, onChange: (e: any) => setBranch(e.currentTarget.value) },
        h("option", { value: "" }, "— pilih —"),
        p.branches().map((b: any) => h("option", { value: b.code },
          b.type === "pusat" ? b.name + " (Stndrt G. Bawah)" : b.name)))) : null,
      field("Kode Item", ItemSelect({ value: item, onSelect: (c: string) => setItem(c), placeholder: "Cari item yang bisa diproduksi…", branch: () => p.isAdmin() ? branch() : "", mode: "producible" })),
      () => minEqPar() ? null : field("Min", h("input", { class: "input", value: min, onInput: (e: any) => setMin(e.currentTarget.value) })),
      field(parLabel(), h("input", { class: "input", value: par, onInput: (e: any) => setPar(e.currentTarget.value) })),
      h("button", { class: "btn btn-primary", onClick: save }, "Simpan"),
    ]),
    h("table", { class: "table", style: "margin-top:14px" },
      h("thead", null, h("tr", null,
        p.isAdmin() ? h("th", null, "Outlet") : null,
        h("th", null, "Item"), h("th", null, "Nama"),
        h("th", { style: "text-align:right" }, "Stok"), minTh(),
        parTh(),
        () => showJadi() ? h("th", { title: "Item finishing: centang agar dipesan JADI dari Pusat (bukan finishing sendiri)" }, "Minta Jadi") : null,
        h("th", { style: "text-align:right" }, "Aksi"))),
      h("tbody", null, () => list().length ? list().map((r) => h("tr", null,
        p.isAdmin() ? h("td", null, r.branch) : null,
        h("td", null, h("b", null, r.item)), h("td", null, r.name || "—"),
        h("td", { style: "text-align:right" }, fmt(r.current)),
        minTd(r.min),
        h("td", { style: "text-align:right" }, fmt(r.par)),
        () => showJadi() ? h("td", null, (r.finishable && r.branch_type === "cabang")
          ? h("input", { type: "checkbox", checked: !!r.minta_jadi, style: "width:17px;height:17px;cursor:pointer",
              title: "Minta jadi ke Pusat", onChange: (e: any) => setJadi(r, e.currentTarget.checked) })
          : null) : null,
        h("td", { style: "text-align:right;white-space:nowrap" },
          h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => edit(r) }, "Ubah"), " ",
          h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => del(r) }, "Hapus"))))
        : h("tr", null, h("td", { colspan: span(), class: "muted", style: "text-align:center;padding:24px" }, "Belum ada data.")))),
    h("div", { style: "margin-top:12px" },
      h("button", { class: "btn btn-ghost", style: "padding:6px 12px;font-size:12.5px", onClick: toggleLog },
        () => showLog() ? "Sembunyikan Riwayat" : "Riwayat Perubahan")),
    () => showLog() ? h("table", { class: "table", style: "margin-top:8px" },
      h("thead", null, h("tr", null, h("th", null, "Waktu"),
        p.isAdmin() ? h("th", null, "Outlet") : null,
        h("th", null, "Item"), h("th", null, "Perubahan"), h("th", null, "Oleh"))),
      h("tbody", null, () => log().length ? log().map((r: any) => h("tr", null,
        h("td", { style: "white-space:nowrap" }, r.when),
        p.isAdmin() ? h("td", null, r.branch) : null,
        h("td", null, h("b", null, r.item), " ", h("span", { class: "muted" }, r.name)),
        h("td", null, chg(r)),
        h("td", null, r.by)))
        : h("tr", null, h("td", { colspan: p.isAdmin() ? 5 : 4, class: "muted", style: "text-align:center;padding:18px" }, "Belum ada perubahan tercatat."))))
      : null,
  ]);
}

function BBThreshSection(p: { isAdmin: () => boolean; branches: () => any[] }) {
  const [list, setList] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [branch, setBranch] = createSignal("");
  const [item, setItem] = createSignal("");
  const [gd, setGd] = createSignal("GDBB");
  const [min, setMin] = createSignal("");
  const load = async () => { try { setList(await api.get("/api/bb-thresholds")); } catch (_) {} };
  onMount(load);
  onLive(["stock"], load);
  const reset = () => { setItem(""); setGd("GDBB"); setMin(""); if (p.isAdmin()) setBranch(""); };
  const save = async () => {
    setMsg(null);
    try {
      await api.send("/api/bb-thresholds", "POST", { branch: branch(), item: item(), gd: gd(), min: +min() || 0 });
      reset(); await load(); setMsg({ ok: true, t: "Tersimpan." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const edit = (r: any) => { if (p.isAdmin()) setBranch(r.branch); setItem(r.item); setGd(r.gd); setMin(String(r.min)); };
  const del = async (r: any) => {
    try { await api.send("/api/bb-thresholds/delete", "POST", { branch: r.branch, item: r.item, gd: r.gd }); await load(); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const span = p.isAdmin() ? 6 : 5;
  return h("div", { class: "card" }, [
    h("h3", null, "Batas Bahan Baku"),
    h("div", { class: "hint" }, "Bahan baku per gudang. Peringatan “Bahan Baku Menipis” muncul saat stok ≤ Min."),
    () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err"), style: "margin-top:10px" }, msg().t) : null,
    h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
      () => p.isAdmin() ? field("Situs", h("select", { class: "input", value: branch, onChange: (e: any) => setBranch(e.currentTarget.value) },
        h("option", { value: "" }, "— pilih —"),
        p.branches().map((b: any) => h("option", { value: b.code }, b.name)))) : null,
      field("Kode Item", ItemSelect({ value: item, onSelect: (c: string) => setItem(c), placeholder: "Cari kode / nama…", branch: () => p.isAdmin() ? branch() : "" })),
      field("Gudang", h("input", { class: "input", style: "max-width:110px", value: gd, onInput: (e: any) => setGd(e.currentTarget.value.toUpperCase()) })),
      field("Min", h("input", { class: "input", value: min, onInput: (e: any) => setMin(e.currentTarget.value) })),
      h("button", { class: "btn btn-primary", onClick: save }, "Simpan"),
    ]),
    h("table", { class: "table", style: "margin-top:14px" },
      h("thead", null, h("tr", null,
        p.isAdmin() ? h("th", null, "Situs") : null,
        h("th", null, "Item"), h("th", null, "Nama"), h("th", null, "Gudang"),
        h("th", { style: "text-align:right" }, "Stok"), h("th", { style: "text-align:right" }, "Min · Aksi"))),
      h("tbody", null, () => list().length ? list().map((r) => h("tr", null,
        p.isAdmin() ? h("td", null, r.branch) : null,
        h("td", null, h("b", null, r.item)), h("td", null, r.name || "—"), h("td", null, r.gd),
        h("td", { style: "text-align:right" }, fmt(r.current)),
        h("td", { style: "text-align:right;white-space:nowrap" },
          h("b", { style: "margin-right:10px" }, fmt(r.min)),
          h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => edit(r) }, "Ubah"), " ",
          h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => del(r) }, "Hapus"))))
        : h("tr", null, h("td", { colspan: span, class: "muted", style: "text-align:center;padding:24px" }, "Belum ada data.")))),
  ]);
}

function ProduksiPage() {
  const [item, setItem] = createSignal("");
  const [qty, setQty] = createSignal("");
  const [branch, setBranch] = createSignal("");
  const [branchOpts, setBranchOpts] = createSignal<any[]>([]);
  const [msg, setMsg] = createSignal<any>(null);
  const [busy, setBusy] = createSignal(false);
  const [list, setList] = createSignal<any>({ rows: [] });
  const isAdmin = isAdminRole;
  const isCabang = isOutletRole;
  const [board, setBoard] = createSignal<any>({ outlets: [], rows: [] });
  const [tasks, setTasks] = createSignal<any[]>([]);
  const [bDate, setBDate] = createSignal(todayISO());
  // Zoom: the sheet is ~25 columns wide, so the client wants an Excel-style
  // slider that fits the whole thing on one screen. CSS `zoom` (not transform:
  // scale) because it reflows layout — scale would keep the original footprint
  // and leave the page scrolling a box that only LOOKS smaller.
  const [zoom, setZoom] = createSignal(100);
  // "Sembunyikan tidak produksi" (revision #4): a view-only declutter that hides
  // rows with no display standard (stndrt == 0). These already sort to the very
  // bottom; the toggle drops them from view entirely. It never touches what a
  // session commits — that reads board().rows regardless (see sesiLines).
  const [hideNonProd, setHideNonProd] = createSignal(false);
  // Per-item overrides for a session's Prdks. The derived number is a proposal
  // — a tray-baked product gets rounded to whole trays — so what the operator
  // sees is what gets committed, keyed sesi|item so the three sessions never
  // overwrite each other.
  const [edit, setEdit] = createSignal<any>({});
  const loadList = async () => { try { setList((await api.get("/api/reports/produksi")) || { rows: [] }); } catch (_) {} };
  // Typed quantities SURVIVE a refresh. This page reloads on every stock /
  // requests / pemesanan broadcast — an agent pushes a snapshot every 30s — so
  // clearing the overrides here would wipe an operator's half-entered session
  // while they were still typing it. They are cleared only when the date
  // changes, where they genuinely belong to a different day.
  const loadBoard = async () => {
    if (isCabang()) return;
    try {
      const b = await api.get("/api/produksi/board?date=" + encodeURIComponent(bDate()));
      if (b && b.rows) setBoard(b); // ignore 401/{_unauth} shapes
    } catch (_) {}
  };
  const pickDate = (v: string) => { setBDate(v); setEdit({}); loadBoard(); };
  const loadTasks = async () => {
    if (!canFinish()) return;
    try { setTasks((await api.get("/api/finishing/tasks")) || []); } catch (_) {}
  };
  onMount(async () => {
    if (isAdmin()) { try { const bs = (await api.get("/api/branches")) || []; setBranchOpts(bs); if (bs[0]) setBranch(bs[0].code); } catch (_) {} }
    loadList(); loadBoard(); loadTasks();
  });
  onLive(["stock"], loadList);
  // NOT "stock": that topic is dominated by agent snapshots, which cannot change
  // anything this board reads (it takes opening stock from stock_opname, never
  // stock_snapshots). "rencana" carries the opening + standards changes that can,
  // and "branches" because the table renders one column per active outlet.
  onLive(["requests", "pemesanan", "rencana", "branches"], loadBoard);
  onLive(["requests", "stock"], loadTasks);
  /* ---- Rencana Produksi: the client's sheet, three sessions in one table ----
     Each session's quantity column is derived by the server and shown editable;
     one "Produksi" press commits that session and locks it. SESI is what the
     three columns differ by — everything else about them is identical, so the
     table is driven from this spec rather than three near-copies. */
  // `derived` is what the column proposes before anyone types; `lock` names the
  // session whose commit freezes the cell. "tbh" is not a session — it is Pusat's
  // hand-keyed on-the-spot need — but it is an editable quantity column with the
  // same behaviour, so it rides the same spec instead of a hand-copied cell.
  const tbhPusat = (r: any) => qtyFor("tbh", r);
  const SESI = [
    { key: "pagi", jam: "08:00", label: "Produksi 08:00", lock: "pagi", derived: (r: any) => +r.prdks || 0 },
    { key: "siang", jam: "11:00", label: "Produksi 11:00", lock: "siang", derived: (r: any) => +r.prdks_1100 || 0 },
    { key: "tbh", jam: "", label: "", lock: "sore", derived: () => 0 },
    // Only the part the leftover cannot cover has to be produced.
    { key: "sore", jam: "14:00", label: "Produksi 14:00", lock: "sore", derived: (r: any) => Math.max(0, tbhPusat(r) - (+r.gb_siang || 0)) },
  ];
  const sesiOf = (k: string) => SESI.find((s) => s.key === k)!;
  const sesiDone = (k: string) => (board().sesi || {})[k];
  // What a row contributes to a column: the operator's override if they typed
  // one, else the derived figure. The derived number is only ever a proposal.
  const qtyFor = (k: string, r: any) => {
    const o = edit()[k + "|" + r.item];
    if (o !== undefined && o !== "") return +o || 0;
    return sesiOf(k).derived(r);
  };
  const setQtyFor = (k: string, item: string, v: string) => setEdit({ ...edit(), [k + "|" + item]: v });
  const sesiLines = (k: string) => (board().rows || [])
    .map((r: any) => ({ item: r.item, qty: qtyFor(k, r) })).filter((l: any) => l.qty > 0);
  const commitSesi = async (k: string) => {
    const lines = sesiLines(k);
    if (!lines.length) { setMsg({ ok: false, t: "Tidak ada item dengan jumlah produksi." }); return; }
    if (!window.confirm("Buat produksi sesi " + k + " untuk " + lines.length + " item?")) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.send("/api/produksi/sesi", "POST", { date: bDate(), sesi: k, lines });
      await Promise.all([loadBoard(), loadList()]);
      setMsg({ ok: true, t: "Produksi sesi " + k + " dibuat: " + r.production_no + " (" + r.lines + " item)." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  // Owner-only: reverse a committed session. Cancel newest-first (the backend
  // enforces it); on success the slot reopens so the day can be re-applied.
  const cancelSesi = async (k: string) => {
    if (!window.confirm("Batalkan produksi sesi " + k + "? Semua dokumen produksi Delphi sesi ini akan dihapus dan stoknya dikembalikan.")) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.send("/api/produksi/cancel", "POST", { date: bDate(), sesi: k });
      await Promise.all([loadBoard(), loadList()]);
      setMsg({ ok: true, t: "Produksi sesi " + k + " (" + r.production_no + ") dibatalkan — " + r.reversed_docs + " dokumen dihapus, stok dikembalikan. Sesi bisa dibuat ulang." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  const askOpening = async () => {
    setMsg(null);
    try {
      await api.send("/api/opname/recompute", "POST", { cutoff: board().opening_date });
      setMsg({ ok: true, t: "Permintaan hitung stok tutup " + board().opening_date + " dikirim — muncul setelah outlet menyinkronkan." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  // Cabang finishing qty comes only from the task list (par − stok) and is never
  // typed; the server recomputes it anyway. Semi on hand does not cap it —
  // finishing proceeds even when the setengah-jadi goes negative (client rule),
  // and the task row says which one will.
  const taskOf = (code: string) => tasks().find((x: any) => x.item === (code || "").toUpperCase());
  const pickTask = (t: any) => { setItem(t.item); setQty(String(t.shortfall)); };
  const submit = async () => {
    if (!item() || !(+qty() > 0)) return;
    setBusy(true); setMsg(null);
    try {
      const body: any = { output_item: item().toUpperCase(), qty: +qty() };
      if (isAdmin()) body.branch = branch();
      const r = await api.send("/api/productions/manual", "POST", body);
      setItem(""); setQty(""); setMsg({ ok: true, t: "Produksi dibuat: " + r.production_no });
      await loadList();
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  return h("div", { class: "main" }, Topbar({ title: () => isCabang() ? "Finishing" : "Produksi", sub: isCabang() ? "Olah setengah jadi & bahan menjadi barang jadi di outlet Anda" : "Buat produksi (masuk Gudang Bawah, kirim lewat Surat Jalan)" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      h("div", { class: "card" }, [
        h("h3", null, "Buat Produksi"),
        h("div", { class: "hint" }, isCabang()
          ? "Menghasilkan barang jadi dari setengah jadi (mis. donat + topping) memakai stok outlet Anda; mencatat Hasil Produksi + HPP."
          : "Menambah barang jadi ke stok. Kirim ke outlet lewat Surat Jalan."),
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:10px" }, [
          () => isAdmin() ? field("Outlet Produksi", h("select", { class: "input", value: branch, onChange: (e: any) => setBranch(e.currentTarget.value) },
            () => branchOpts().map((b: any) => h("option", { value: b.code }, b.name)))) : null,
          field("Kode Produk", ItemSelect({ value: item, onSelect: (c: string) => { setItem(c); if (canFinish()) setQty(String(taskOf(c)?.shortfall ?? 0)); }, placeholder: canFinish() ? "Cari item finishing…" : "Cari kode / nama produk…", branch: () => isAdmin() ? branch() : "", mode: canFinish() ? "finish" : undefined })),
          field("Jumlah", canFinish()
            ? [h("input", { class: "input", value: qty, readOnly: true, style: "background:var(--bg-soft,#f5f0e8);cursor:not-allowed", title: "Jumlah ditentukan Batas Stok — tidak bisa diubah manual." }),
               h("div", { class: "hint", style: "margin-top:4px" }, "Terkunci: " + parLabel() + " − stok. Ubah lewat Batas Stok (tercatat).")]
            : h("input", { class: "input", value: qty, onInput: (e: any) => setQty(e.currentTarget.value) })),
          h("button", { class: "btn btn-primary", disabled: busy, onClick: submit }, () => busy() ? h("span", { class: "spin" }) : null, () => isCabang() ? "Finishing" : "Produksi"),
        ]),
      ]),
      // Finishing tasks (cabang): finishable items below their display minimum.
      // Wajib finishing sendiri — these never appear on a permintaan to Pusat.
      () => (canFinish() && tasks().length) ? h("div", { class: "card", style: "margin-top:18px;border-color:var(--brand-soft);background:#FFF8EF" }, [
        h("div", null, h("h3", { style: "margin:0" }, "✦ Tugas Finishing (" + tasks().length + " item)"),
          h("div", { class: "hint" }, "Item finishing di bawah batas display — olah dari stok setengah jadi outlet Anda. Jumlah mengikuti Perlu.")),
        h("table", { class: "table", style: "margin-top:10px" },
          h("thead", null, h("tr", null, h("th", null, "Item"),
            h("th", { style: "text-align:right" }, "Stok"), minTh(),
            parTh(), h("th", { style: "text-align:right" }, "Perlu"),
            h("th", { style: "text-align:right" }, "Semi Cukup"), h("th", null, ""))),
          h("tbody", null, () => tasks().map((t: any) => h("tr", null,
            h("td", null, h("b", null, t.item), " ", h("span", { class: "muted" }, t.name),
              semiShort(t)
                ? h("div", { style: "margin-top:3px;color:" + SEMI_INK + ";font-size:12px;font-weight:500" }, semiShortText(t))
                : null),
            h("td", { style: "text-align:right" }, fmt(t.current)),
            minTd(t.min),
            h("td", { style: "text-align:right" }, fmt(t.par)),
            h("td", { style: "text-align:right" }, h("b", { style: "color:var(--brand-strong)" }, fmt(t.shortfall))),
            h("td", { style: "text-align:right" }, h("span", { style: semiShort(t) ? "color:" + SEMI_INK + ";font-weight:700" : "" }, fmt(t.semi_cap))),
            h("td", { style: "text-align:right" },
              h("button", { class: "btn btn-ghost", style: "padding:4px 10px;font-size:12px", disabled: !(+t.shortfall > 0), onClick: () => pickTask(t) }, "Finishing")))))),
      ]) : null,
      // Rencana Produksi — the client's daily sheet, all three sessions in one
      // table. Column groups follow the sheet exactly so the operator can read
      // it the way they always have.
      () => {
        const b = board();
        if (isCabang()) return null;
        // hideNonProd is view-only: commit still reads board().rows in full.
        const rows = hideNonProd() ? (b.rows || []).filter((r: any) => +r.stndrt > 0) : (b.rows || []);
        const sum = (f: (r: any) => number) => rows.reduce((a: number, r: any) => a + (f(r) || 0), 0);
        const outlets = b.outlets || [];
        // Header cell for a group spanning several outlet columns.
        const grp = (label: string, span: number, tone?: string) =>
          h("th", { colspan: span, style: "text-align:center;border-left:2px solid var(--line)" + (tone || "") }, label);
        const sesiBtn = (s: any) => {
          const no = sesiDone(s.key);
          if (no) return h("span", { class: "row", style: "gap:6px;align-items:center" },
            h("span", { class: "badge cabang" }, "✓ " + no),
            // Only an owner sees Batalkan; the backend also enforces owner +
            // newest-first + not-yet-dispatched + same-day.
            () => isOwnerRole() ? h("button", {
              class: "btn btn-ghost", style: "padding:3px 8px;font-size:11px;color:#b5453b",
              disabled: busy, onClick: () => cancelSesi(s.key),
            }, "Batalkan") : null);
          return h("button", {
            class: "btn btn-primary", style: "padding:4px 10px;font-size:12px",
            disabled: busy, onClick: () => commitSesi(s.key),
          }, s.label);
        };
        // One editable quantity cell. Locked once its session is committed —
        // the goods are in Delphi by then and re-running would double the day.
        const qtyCell = (k: string, r: any, edge?: string) => {
          if (sesiDone(sesiOf(k).lock)) return numTd(qtyFor(k, r), ";color:var(--sage)" + (edge || ""));
          return h("td", { style: "text-align:right" + (edge || "") },
            h("input", {
              class: "input", style: "width:76px;text-align:right;padding:4px 6px;font-size:12px",
              value: () => String(qtyFor(k, r) || ""),
              onChange: (e: any) => setQtyFor(k, r.item, e.currentTarget.value),
            }));
        };
        return h("div", { class: "card", style: "margin-top:18px;border-color:var(--brand-soft);background:#FFF8EF" }, [
          h("div", { class: "row between", style: "align-items:center;flex-wrap:wrap;gap:10px" },
            h("div", null, h("h3", { style: "margin:0" }, "✦ Rencana Produksi"),
              h("div", { class: "hint" },
                "Pesanan diambil sebelum jam " + (b.cutoff || "12:00") + " diproduksi H-1; yang diambil setelahnya masuk sesi pagi hari itu. Stok awal = stok tutup " + (b.opening_date || "") + ".")),
            h("div", { class: "row", style: "gap:10px;align-items:center;flex-wrap:wrap" },
              field("Tanggal", h("input", {
                class: "input", type: "date", style: "max-width:160px",
                value: bDate, onChange: (e: any) => pickDate(e.currentTarget.value),
              })),
              field("Zoom", h("div", { class: "row", style: "gap:6px;align-items:center" },
                h("input", {
                  type: "range", min: "40", max: "100", step: "5", style: "width:130px",
                  value: () => String(zoom()), onInput: (e: any) => setZoom(+e.currentTarget.value),
                }),
                h("span", { class: "muted", style: "font-size:12px;min-width:38px" }, () => zoom() + "%"))),
              h("label", { class: "row", style: "gap:6px;align-items:center;font-size:12px;cursor:pointer" },
                h("input", { type: "checkbox", checked: hideNonProd,
                  onChange: (e: any) => setHideNonProd(e.currentTarget.checked) }),
                "Sembunyikan tidak produksi"),
              xlsxBtn(() => "RencanaProduksi-" + bDate(),
                ["Golongan", "Kode", "Nama", "Stok G.B", "Stok Outlet", "Stndrt G.B", "Psn Nnt Sore",
                  ...outlets.map((o: any) => "Psn " + o.code), "± PSN", "± Std Dsply G.B", "Prdks", "G.B 08:00",
                  ...outlets.map((o: any) => "Tbh " + o.code), "± Tbhn Pro", "Prdks 11:00", "Lbhn",
                  "Tbh Outlet PUSAT", "Prdks 14:00"],
                () => rows.map((r: any) => [r.nama_gol || "", r.item, r.name, +r.stok_gb, +r.stok_outlet, +r.stndrt, +r.psn_sore,
                  ...outlets.map((o: any) => +(r.pesanan?.[o.code] || 0)), +r.l, +r.m, qtyFor("pagi", r), +r.gb_0800,
                  ...outlets.map((o: any) => +(r.tambahan?.[o.code] || 0)), +r.t, qtyFor("siang", r), +r.gb_siang,
                  tbhPusat(r), qtyFor("sore", r)]))),
          ),
          // Stok awal is the previous day's CLOSE, derived by the outlet's agent
          // from Delphi movement history. Absent is not zero: a table built on
          // zeroes would order the full standard for every item in the catalog,
          // so say so and offer to ask for it rather than render a plausible lie.
          () => !b.opening_items ? h("div", { class: "alert err", style: "margin-top:12px" },
            "Belum ada data stok tutup " + (b.opening_date || "") + " — menunggu sinkronisasi dari outlet. Stok G.B / Stok Outlet di bawah masih 0, jadi jumlah produksi belum bisa dipakai. ",
            h("button", { class: "btn btn-ghost", style: "padding:2px 10px;font-size:12px;margin-left:6px", onClick: askOpening }, "Minta hitung ulang"))
            : h("div", { class: "hint", style: "margin-top:10px" },
                "Stok awal dari stok tutup " + (b.opening_date || "") + " — " + b.opening_items + " item. ",
                h("button", { class: "btn btn-ghost", style: "padding:2px 10px;font-size:12px", onClick: askOpening }, "Hitung ulang")),
          () => !rows.length ? h("div", { class: "muted", style: "margin-top:12px" }, "Tidak ada yang perlu diproduksi untuk tanggal ini.") : null,
          () => rows.length ? h("div", { class: "row", style: "gap:8px;margin-top:12px;flex-wrap:wrap;align-items:center" },
            // Only the three real sessions get a commit button; "tbh" shares the
            // spec because it is an editable quantity column, not a session.
            SESI.filter((s) => s.jam).map((s) => h("div", { class: "row", style: "gap:6px;align-items:center" },
              h("span", { class: "muted", style: "font-size:12px" }, "Sesi " + s.jam), sesiBtn(s)))) : null,
          // zoom shrinks the whole grid to fit the page; the wrapper still
          // scrolls for the zoom levels that don't quite make it.
          () => rows.length ? h("div", { style: () => "margin-top:10px;overflow-x:auto;zoom:" + (zoom() / 100) },
            h("table", { class: "table", style: "min-width:100%" }, [
              h("thead", null, [
                h("tr", null,
                  h("th", { colspan: 3 }, ""),
                  grp("PESANAN", outlets.length + 1),
                  h("th", { colspan: 4, style: "border-left:2px solid var(--line)" }, ""),
                  grp("TAMBAHAN", outlets.length),
                  h("th", { colspan: 5, style: "border-left:2px solid var(--line)" }, "")),
                h("tr", null,
                  h("th", null, "Item"),
                  h("th", { style: "text-align:right" }, "Stok G.B"),
                  h("th", { style: "text-align:right" }, "Stok Outlet"),
                  h("th", { style: "text-align:right;border-left:2px solid var(--line)" }, "Psn Nnt Sore"),
                  outlets.map((o: any) => h("th", { style: "text-align:right", title: o.name }, o.code)),
                  h("th", { style: "text-align:right;border-left:2px solid var(--line)" }, "± PSN"),
                  h("th", { style: "text-align:right" }, "± Std Dsply"),
                  h("th", { style: "text-align:right" }, "Prdks"),
                  h("th", { style: "text-align:right" }, "G.B 08:00"),
                  outlets.map((o: any) => h("th", { style: "text-align:right;border-left:2px solid var(--line)", title: o.name }, o.code)),
                  h("th", { style: "text-align:right;border-left:2px solid var(--line)" }, "± Tbhn"),
                  h("th", { style: "text-align:right" }, "Prdks 11:00"),
                  h("th", { style: "text-align:right" }, "Lbhn"),
                  h("th", { style: "text-align:right;border-left:2px solid var(--line)" }, "Tbh Outlet PUSAT"),
                  h("th", { style: "text-align:right" }, "Prdks 14:00")),
              ]),
              h("tbody", null, rows.map((r: any, i: number) => {
                const prev = i > 0 ? rows[i - 1] : null;
                // Rules follow the SORT's real structure, not category — the
                // sheet is ordered donat → by gramasi → non-producing, so a
                // category divider would fire on almost every row. Draw one only
                // at the two boundaries that mean something: the end of the DONAT
                // block, and the line below which nothing has a display standard.
                const band = (x: any) =>
                  +x.stndrt <= 0 ? 2 : (String(x.kategori).toUpperCase() === "DONAT" ? 0 : 1);
                const bandBreak = !!prev && band(prev) !== band(r);
                return h("tr", { style: bandBreak ? "border-top:2px solid var(--brand-soft)" : "" }, [
                  h("td", null, h("b", null, r.item), " ", h("span", { class: "muted" }, r.name),
                    r.wajib_h1 ? h("span", { class: "badge cabang", style: "margin-left:6px;font-size:10px" }, "H-1") : null,
                    r.gramasi ? h("span", { class: "muted", style: "margin-left:6px;font-size:11px" }, r.gramasi + "g") : null,
                    // Category shown per-row (not as a group header): the gramasi
                    // sort interleaves categories, so each row carries its own tag.
                    r.kategori || r.nama_gol
                      ? h("span", { class: "muted", style: "margin-left:6px;font-size:11px;opacity:.7" }, r.kategori || r.nama_gol) : null),
                  numTd(r.stok_gb), numTd(r.stok_outlet),
                  h("td", { style: "text-align:right;border-left:2px solid var(--line)" }, fmt(r.psn_sore)),
                  outlets.map((o: any) => numTd(r.pesanan?.[o.code] || 0)),
                  numTd(r.l, ";border-left:2px solid var(--line)" + (+r.l < 0 ? ";color:#b5453b" : "")),
                  numTd(r.m, +r.m < 0 ? ";color:#b5453b" : ""),
                  qtyCell("pagi", r),
                  numTd(r.gb_0800),
                  outlets.map((o: any) => numTd(r.tambahan?.[o.code] || 0, ";border-left:2px solid var(--line)")),
                  numTd(r.t, ";border-left:2px solid var(--line)" + (+r.t < 0 ? ";color:#b5453b" : "")),
                  qtyCell("siang", r),
                  numTd(r.gb_siang),
                  qtyCell("tbh", r, ";border-left:2px solid var(--line)"),
                  qtyCell("sore", r),
                ]);
              })),
              h("tfoot", null, h("tr", { style: "font-weight:600;border-top:2px solid var(--brand-soft)" },
                h("td", null, "Total"),
                numTd(sum(r => +r.stok_gb)), numTd(sum(r => +r.stok_outlet)),
                numTd(sum(r => +r.psn_sore), ";border-left:2px solid var(--line)"),
                outlets.map((o: any) => numTd(sum(r => +(r.pesanan?.[o.code] || 0)))),
                h("td", { style: "border-left:2px solid var(--line)" }), h("td", null),
                // The four totals that read edit() are passed as THUNKS. Called
                // eagerly they would subscribe this whole card to the override
                // map, so every keystroke-commit would tear down and rebuild all
                // ~21 cells of every row instead of updating four numbers.
                numTd(() => sum(r => qtyFor("pagi", r))),
                numTd(sum(r => +r.gb_0800)),
                outlets.map((o: any) => numTd(sum(r => +(r.tambahan?.[o.code] || 0)), ";border-left:2px solid var(--line)")),
                h("td", { style: "border-left:2px solid var(--line)" }),
                numTd(() => sum(r => qtyFor("siang", r))),
                numTd(sum(r => +r.gb_siang)),
                numTd(() => sum(r => tbhPusat(r)), ";border-left:2px solid var(--line)"),
                numTd(() => sum(r => qtyFor("sore", r))))),
            ])) : null,
        ]);
      },
      h("div", { class: "card", style: "margin-top:18px;padding:6px 0 0;overflow-x:auto" }, [
        h("div", { class: "row between", style: "padding:12px 22px 2px;align-items:center" },
          h("h3", { style: "margin:0" }, "Daftar Produksi"),
          xlsxBtn(() => "DaftarProduksi-" + dstamp(),
            ["Tgl", "No Produksi", "Outlet", "Kode", "Nama", "Satuan", "Jumlah"],
            () => (list().rows || []).map((r: any) => [r.date, r.production_no, r.branch_name || r.branch, r.item, r.name, r.unit || "", +r.qty || 0]))),
        h("table", { class: "table", style: "margin-top:6px" },
          h("thead", null, h("tr", null,
            h("th", { style: "padding-left:22px" }, "Tgl"), h("th", null, "No Produksi"),
            () => isAdmin() ? h("th", null, "Outlet") : null,
            h("th", null, "Kode"), h("th", null, "Nama"), h("th", null, "Satuan"),
            h("th", { style: "text-align:right;padding-right:22px" }, "Jumlah"))),
          h("tbody", null, () => (list().rows && list().rows.length)
            ? list().rows.map((r: any) => h("tr", null,
                h("td", { style: "padding-left:22px" }, r.date),
                h("td", null, h("b", null, r.production_no)),
                () => isAdmin() ? h("td", null, r.branch_name || r.branch) : null,
                h("td", null, r.item),
                h("td", null, r.name || "—"),
                h("td", null, r.unit || h("span", { class: "muted" }, "—")),
                h("td", { style: "text-align:right;padding-right:22px;font-variant-numeric:tabular-nums" }, fmt(r.qty))))
            : h("tr", null, h("td", { colspan: isAdmin() ? 7 : 6, class: "muted", style: "text-align:center;padding:26px" }, "Belum ada produksi.")))),
      ]),
    ]));
}

function SuratJalanPage() {
  const [branches, setBranches] = createSignal<any[]>([]);
  const [stock, setStock] = createSignal<any[]>([]);
  const [reqs, setReqs] = createSignal<any[]>([]);
  const [list, setList] = createSignal<any[]>([]);
  const [recent, setRecent] = createSignal<any[]>([]);
  const [toBranch, setToBranch] = createSignal("");
  const [sourceGd, setSourceGd] = createSignal("GD1"); // GD Bawah: staging hasil produksi
  const [lines, setLines] = createSignal<any[]>([]);    // bahan baku only — keyed in by hand
  const [item, setItem] = createSignal("");
  const [qty, setQty] = createSignal("");
  const [kind, setKind] = createSignal("permintaan");
  const [pesanan, setPesanan] = createSignal<any[]>([]);
  const [note, setNote] = createSignal("");
  const [msg, setMsg] = createSignal<any>(null);
  const [busy, setBusy] = createSignal(false);

  const [fBranch, setFBranch] = createSignal("");
  const [fStatus, setFStatus] = createSignal("");
  const [fKind, setFKind] = createSignal("");
  const [fQ, setFQ] = createSignal("");
  const loadStock = async () => { try { setStock(await api.get("/api/stock?branch=PST") || []); } catch (_) {} };
  const loadList = async () => {
    const p = new URLSearchParams();
    if (fBranch()) p.set("branch", fBranch());
    if (fStatus()) p.set("status", fStatus());
    if (fKind()) p.set("kind", fKind());
    if (fQ()) p.set("q", fQ());
    try { setList(await api.get("/api/deliveries?" + p.toString()) || []); } catch (_) {}
  };
  const sent = () => (list() || []).filter((d: any) => d.status !== "draft");
  const kindBadge = (k: string) => h("span", { class: "badge " + (SJ_KIND[k]?.cls || ""), style: "margin-left:6px" }, kindLabel(k));
  // Declared before the memos below: createMemo evaluates eagerly, so a memo
  // reading manualKind would hit its temporal dead zone during setup.
  const manualKind = () => !!SJ_KIND[kind()]?.manualLines;
  // The draft card and the picker's "already dispatched today" hint both come
  // from ONE unfiltered fetch — list() carries the archive's filters and cannot
  // answer either. Deriving beats fetching twice more: reload() runs on every
  // quantity trim and every deliveries broadcast.
  const loadRecent = async () => {
    try { setRecent(await api.get("/api/deliveries") || []); } catch (_) {}
  };
  const reload = () => Promise.all([loadList(), loadRecent()]);
  const drafts = createMemo(() => recent().filter((d: any) => d.status === "draft"));
  // cabang code → today's dispatched sj_no, for the current kind. Without it the
  // picker offers rows the server will refuse (or silently turns into a susulan)
  // and the operator only finds out after clicking.
  const dispatched = createMemo(() => {
    const t = todayISO(), m: any = {};
    if (!manualKind()) {
      for (const d of recent()) if (d.kind === kind() && d.when === t && d.status !== "draft") m[d.to] = d.sj_no;
    }
    return m;
  });
  // A ticked source can't go today only when susulan is off.
  const sjBlocked = (branchCode: string) => !!dispatched()[branchCode] && !extraSJ();
  const sjNote = (branchCode: string) => {
    const no = dispatched()[branchCode];
    if (!no) return null;
    return h("div", { style: "font-size:11.5px;margin-top:2px;color:" + (extraSJ() ? SEMI_INK : "#b5453b") },
      no + " sudah dikirim — " + (extraSJ() ? "ini akan menjadi SJ susulan hari ini." : "masuk SJ besok."));
  };
  const loadReqs = async () => {
    // only requests still awaiting their (single, complete) Surat Jalan
    try { setReqs(await api.get("/api/requests?status=terbuka") || []); } catch (_) {}
  };
  const loadPesanan = async () => {
    // open cabang orders that still need their SJ (server-side filter)
    try { setPesanan((await api.get("/api/pemesanan?status=open&sendable=1")) || []); } catch (_) {}
  };
  onMount(async () => {
    try {
      const bs = (await api.get("/api/branches") || []);
      // cabang first; the Pusat outlet is a valid destination too (GD Bawah → GD)
      setBranches([...bs.filter((b: any) => b.type === "cabang"), ...bs.filter((b: any) => b.type === "pusat")]);
      const c = bs.find((b: any) => b.type === "cabang"); if (c) setToBranch(c.code);
    } catch (_) {}
    await Promise.all([loadReqs(), loadPesanan(), loadStock(), reload()]);
    await autoDraft();
  });
  onLive(["stock"], loadStock);
  // "stock" too, not just "deliveries": reload() is what carries avail/claimed,
  // and the events that free up stock (agent snapshot push, produksi selesai)
  // broadcast only "stock". Without this an open draft keeps a page-load
  // snapshot, so the send dialog goes on refusing goods that have since arrived.
  onLive(["deliveries", "stock"], reload);
  onLive(["requests"], loadReqs);
  onLive(["pemesanan"], loadPesanan);

  const cabangCodes = () => new Set(branches().filter((b: any) => b.type === "cabang").map((b: any) => b.code));
  const gdList = () => { const s = new Set(stock().map((x: any) => x.gd)); return [...s].filter((g: any) => !cabangCodes().has(g)).sort(); };
  // EXACT availability for the items in play — fetched uncapped by item code, so
  // it's correct even when the source item sits past the /api/stock 400-row
  // window on a large catalog (the bulk stock() list reports 0 there and would
  // wrongly block a valid Surat Jalan).
  const [extra, setExtra] = createSignal<any>({}); // "code|gd" -> stock row
  const loadAvail = async (codes: string[]) => {
    const cs = [...new Set(codes.filter(Boolean).map((c) => String(c).toUpperCase()))];
    if (!cs.length) return;
    try {
      const rs = (await api.get("/api/stock?branch=PST&codes=" + encodeURIComponent(cs.join(",")))) || [];
      const m = { ...extra() };
      for (const r of rs) m[r.code + "|" + r.gd] = r;
      setExtra(m);
    } catch (_) {}
  };
  // Key on the sorted unique code SET, not lines() — editing a qty rebuilds
  // lines() but must not refetch availability.
  const lineCodes = createMemo(() => [...new Set(lines().map((l: any) => l.item).filter(Boolean))].sort().join(","));
  createEffect(() => { const k = lineCodes(); if (k) loadAvail(k.split(",")); });
  const availAt = (it: string, gd: string) => {
    const e = extra()[it + "|" + gd];
    if (e) return e.qty;
    const r = stock().find((s: any) => s.code === it && s.gd === gd);
    return r ? r.qty : 0;
  };
  // name/unit for a line: prefer the exact fetched rows, then the loaded stock
  // list; preloaded permintaan/pesanan lines already carry them.
  const stockByCode = createMemo(() => { const m: any = {}; for (const s of stock()) if (!m[s.code]) m[s.code] = s; return m; });
  const extraByCode = createMemo(() => { const m: any = {}, ex = extra(); for (const k in ex) if (!m[ex[k].code]) m[ex[k].code] = ex[k]; return m; });
  const metaOf = (code: string) => extraByCode()[code] || stockByCode()[code] || {};
  const addLine = () => {
    const it = item().toUpperCase(); if (!it || !(+qty() > 0)) return;
    const ex = lines().find((l: any) => l.item === it);
    if (ex) setLines(lines().map((l: any) => l.item === it ? { ...l, qty: +l.qty + +qty() } : l));
    else setLines([...lines(), { item: it, qty: +qty() }]);
    setItem(""); setQty("");
  };
  // Drafts are fetched on their own, NOT filtered out of list(): the archive
  // below carries the user's outlet/status/search filters, and picking
  // "Dikirim" there would otherwise blank the draft card — which is the
  // primary work surface on this page.

  // The source lists the picker and the drafts both depend on.
  const srcList = () => manualKind() ? [] : kind() === "pesanan" ? pesanan() : reqs();
  const refresh = () => Promise.all([reload(), loadReqs(), loadPesanan()]);
  // One cabang, everything it has waiting. The server decides WHICH sources go
  // in — the client only names the outlet, so a subset is not expressible and
  // the day's SJ cannot fragment into one document per permintaan.
  const susunSJ = async (branchCode: string) => {
    setMsg(null);
    const body: any = { kind: kind(), to_branch: branchCode, note: note() };
    if (manualKind()) {
      const ls = lines().map((l: any) => ({ item: l.item, qty: +l.qty || 0 })).filter((l: any) => l.qty > 0);
      if (!ls.length) { setMsg({ ok: false, t: "Tambahkan minimal satu item." }); return; }
      // source_gd only matters here — the server forces GD1 for the other kinds.
      body.lines = ls; body.source_gd = sourceGd();
    }
    setBusy(true);
    try {
      const r = await api.send("/api/deliveries/draft", "POST", body);
      setLines([]); setNote("");
      await refresh();
      setMsg({ ok: true, t: "Masuk draft " + r.sj_no + " → " + r.to + " (" + r.lines_added + " baris)" });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  // Sources waiting per cabang, grouped once and read for both the count and
  // the detail column.
  const pendingBy = createMemo(() => {
    const m: any = {};
    for (const s of srcList()) (m[s.branch] = m[s.branch] || []).push(s);
    return m;
  });
  // Auto-draft: ONE call assembles every cabang's document, so the operator
  // arrives at drafts to review rather than buttons to press. The server
  // enumerates the outlets — leaving that loop here would put "which cabang get
  // a document" back on the client, one level above the choice the draft
  // endpoint exists to take away from it.
  const autoDraft = async () => {
    if (!settings().auto_draft_sj || manualKind()) return;
    try { await api.send("/api/deliveries/draft-all", "POST", { kind: kind() }); } catch (_) {}
    await refresh();
  };
  // qty <= 0 removes the line ("keluarkan item dari daftar SJ"); emptying a
  // draft deletes it server-side so it stops holding today's slot.
  const setLineQty = async (lineID: number, q: number) => {
    setMsg(null);
    try { await api.send("/api/deliveries/line", "POST", { line_id: lineID, qty: q }); await Promise.all([reload(), loadReqs()]); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  // Items this SJ overdraws, deduped per item. l.claimed is the server's own
  // per-item total — the same field the red Tersedia cell tests below, so the
  // dialog names exactly the rows the table already marks. This is a pre-flight
  // over the payload snapshot, not the ruling: sendSJ re-checks against stock
  // and its refusal text wins whenever the two disagree.
  const overStock = (d: any) => {
    const byItem: any = {};
    for (const l of d.lines || [])
      if (+l.claimed > +l.avail) byItem[l.item] = { item: l.item, name: l.name, avail: +l.avail, kirim: +l.claimed };
    return Object.values(byItem);
  };
  const [shortfall, setShortfall] = createSignal<any>(null);
  const sendDraft = async (d: any) => {
    setMsg(null);
    if (overStock(d).length) { setShortfall(d); return; }
    setBusy(true);
    try {
      const r = await api.send("/api/deliveries/send", "POST", { delivery_id: d.id });
      await Promise.all([reload(), loadStock(), loadReqs()]);
      setMsg({ ok: true, t: "Surat Jalan dikirim: " + r.sj_no + " → " + r.to });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setBusy(false);
  };
  const printSJ = (d: any) => {
    const w = window.open("", "_blank", "width=900,height=760"); if (!w) return;
    const co = settings().company_name || "Ajeng Bakery";
    const tag = settings().tagline || "";
    const logo = settings().has_logo ? `<img class=logo src="${location.origin}/api/logo?v=${logoV()}" alt="" />` : `<div class=logomark>🥐</div>`;
    const items = d.lines || [];
    // A pesanan SJ carries several customers' orders, so each row names its
    // order and whether the customer collects it at the outlet or has it
    // delivered — the outlet needs that on paper to route the goods.
    const isPes = d.kind === "pesanan";
    let totalQty = 0;
    const rows = items.map((l: any, i: number) => {
      totalQty += Number(l.qty) || 0;
      // Revision #6: lines arrive grouped by pesanan no. then kode barang. A
      // blank spacer row separates one customer's order from the next so the
      // outlet can tell the orders apart at a glance on paper.
      const newGroup = isPes && i > 0 && (l.pesanan_no || "") !== (items[i - 1].pesanan_no || "");
      const gap = newGroup ? `<tr class=gap><td colspan=7></td></tr>` : "";
      const pes = isPes ? `<td>${l.pesanan_no || "—"}${l.pesanan_cust ? " · " + l.pesanan_cust : ""}${l.jenis_kirim ? `<br><span class=kir>${kirimLabel(l.jenis_kirim)}</span>` : ""}</td>` : "";
      // Revision #5: an empty box on every line, ticked by hand as the picker
      // packs each item. Print-only — no state is stored for it.
      return `${gap}<tr><td class=c>${i + 1}</td><td class=code>${l.item}</td><td>${l.name || "—"}</td>${pes}<td>${l.unit || "—"}</td><td class=r>${fmt(l.qty)}</td><td class=chk></td></tr>`;
    }).join("");
    const noteHtml = d.note ? `<div class=note><span>Catatan</span> ${d.note}</div>` : "";
    const html = `<!doctype html><html><head><meta charset=utf-8><title>Surat Jalan ${d.sj_no}</title><style>
      @page { size: A4; margin: 14mm; }
      * { box-sizing: border-box; }
      html,body { margin:0; }
      body { font-family:'Segoe UI',Helvetica,Arial,sans-serif; color:#3a2a1c; font-size:12.5px; line-height:1.5; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      .doc { max-width:740px; margin:0 auto; padding:8px; }
      .top { display:flex; justify-content:space-between; align-items:flex-start; gap:24px; padding-bottom:16px; border-bottom:3px solid #a9601f; }
      .brand { display:flex; align-items:center; gap:14px; }
      .logo, .logomark { width:60px; height:60px; border-radius:12px; flex:none; object-fit:contain; }
      .logomark { display:flex; align-items:center; justify-content:center; font-size:30px; background:linear-gradient(140deg,#e6a95a,#a9601f); }
      .co { font-family:Georgia,'Times New Roman',serif; font-size:23px; font-weight:700; color:#2c1d10; line-height:1.05; }
      .tag { font-size:11px; color:#9a8a78; margin-top:2px; }
      .docbox { text-align:right; }
      .docbox .lbl { font-size:19px; font-weight:800; letter-spacing:.14em; color:#a9601f; }
      .docbox .no { font-size:15px; font-weight:700; margin-top:3px; }
      .docbox .dt { font-size:11.5px; color:#7a6a58; margin-top:1px; }
      .parties { display:flex; gap:14px; margin-top:18px; }
      .party { flex:1; border:1px solid #e8dcc9; border-radius:11px; padding:12px 15px; background:#fdf9f3; }
      .party .h { font-size:10px; text-transform:uppercase; letter-spacing:.12em; color:#a9601f; font-weight:700; margin-bottom:5px; }
      .party .v { font-size:15px; font-weight:700; color:#2c1d10; }
      .party .s { font-size:12px; color:#7a6a58; margin-top:1px; }
      table { width:100%; border-collapse:collapse; margin-top:20px; }
      thead th { background:#a9601f; color:#fff; font-size:10.5px; text-transform:uppercase; letter-spacing:.06em; font-weight:700; padding:10px 12px; text-align:left; }
      thead th.r { text-align:right; } thead th.c { text-align:center; }
      thead th:first-child { border-radius:8px 0 0 0; } thead th:last-child { border-radius:0 8px 0 0; }
      tbody td { padding:9px 12px; border-bottom:1px solid #eee3d3; }
      tbody tr:nth-child(even) td { background:#fbf6ee; }
      tr.gap td { height:12px; padding:0; border:0; background:#fff !important; }
      td.chk { text-align:center; }
      td.chk::before { content:""; display:inline-block; width:15px; height:15px; border:1.5px solid #9a8a78; border-radius:3px; vertical-align:middle; }
      td.r { text-align:right; font-variant-numeric:tabular-nums; font-weight:600; }
      td.c { text-align:center; color:#9a8a78; } td.code { font-weight:700; }
      tfoot td { padding:11px 12px; font-weight:700; border-top:2px solid #a9601f; color:#2c1d10; }
      tfoot td.r { color:#a9601f; }
      .kir { font-size:10.5px; color:#a9601f; font-weight:700; }
      .note { margin-top:15px; font-size:12.5px; background:#fbf3e7; border-left:3px solid #d9a25a; border-radius:0 8px 8px 0; padding:9px 13px; }
      .note span { color:#a9601f; font-weight:700; }
      .signs { display:flex; justify-content:space-between; gap:24px; margin-top:52px; text-align:center; }
      .signs .sg { flex:1; }
      .signs .role { font-size:11.5px; color:#7a6a58; }
      .signs .box { height:56px; }
      .signs .line { border-top:1px solid #3a2a1c; padding-top:5px; font-size:12px; font-weight:600; }
      .foot { margin-top:34px; text-align:center; font-size:10px; color:#b3a595; border-top:1px solid #eee3d3; padding-top:10px; }
    </style></head><body><div class=doc>
      <div class=top>
        <div class=brand>${logo}<div><div class=co>${co}</div><div class=tag>${tag || "Surat Jalan / Delivery Note"}</div></div></div>
        <div class=docbox><div class=lbl>SURAT JALAN</div><div class=no>${d.sj_no}</div><div class=dt>${kindLabel(d.kind)} · Tanggal kirim: ${d.when || ""}</div></div>
      </div>
      <div class=parties>
        <div class=party><div class=h>Dari</div><div class=v>${co}</div><div class=s>Gudang asal: ${d.source_gd || "GD1"}</div></div>
        <div class=party><div class=h>Dikirim Kepada</div><div class=v>${d.to}</div><div class=s>Outlet penerima</div></div>
      </div>
      <table>
        <thead><tr><th class=c style="width:38px">No</th><th style="width:96px">Kode</th><th>Nama Barang</th>${isPes ? "<th>Pesanan / Pelanggan</th>" : ""}<th style="width:80px">Satuan</th><th class=r style="width:96px">Jumlah</th><th class=c style="width:44px">Cek</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan=${isPes ? 5 : 4} class=r>Total (${items.length} baris)</td><td class=r>${fmt(totalQty)}</td><td></td></tr></tfoot>
      </table>
      ${noteHtml}
      <div class=signs>
        <div class=sg><div class=role>Hormat kami, Pengirim</div><div class=box></div><div class=line>Petugas Gudang</div></div>
        <div class=sg><div class=role>Diangkut oleh</div><div class=box></div><div class=line>Sopir</div></div>
        <div class=sg><div class=role>Diterima oleh</div><div class=box></div><div class=line>Penerima (${d.to})</div></div>
      </div>
      <div class=foot>Dicetak dari ${co} Central · Dokumen sah bila disertai stempel perusahaan.</div>
    </div></body></html>`;
    w.document.write(html);
    w.document.close(); setTimeout(() => w.print(), 400);
  };

  // Blocked-send dialog. Deliberately modal rather than an inline warning: the
  // send did NOT happen, and that has to be acknowledged rather than scrolled
  // past — an operator who thinks the SJ went out stops watching it. Reported as
  // a group, because an aggregated SJ is a whole cabang's day and fixing one
  // problem per press of Kirim is miserable.
  const shortfallModal = () => {
    const d = shortfall();
    if (!d) return null;
    const bad = overStock(d) as any[];
    return h("div", {
      // above the dropdown (40) on theme.css's scale: topbar 5, mobile nav 30.
      style: "position:fixed;inset:0;background:rgba(38,26,16,.45);display:flex;align-items:center;justify-content:center;padding:24px;z-index:50",
      onClick: (e: any) => { if (e.target === e.currentTarget) setShortfall(null); } },
      h("div", { class: "card", style: "max-width:640px;width:100%;margin:0;max-height:80vh;overflow:auto" }, [
        h("h3", { style: "margin:0" }, "Belum bisa dikirim — stok tidak cukup"),
        h("div", { class: "hint", style: "margin-top:6px" },
          "Surat Jalan " + d.sj_no + " → " + d.to + " punya " + bad.length +
          " item yang melebihi stok " + (d.source_gd || "GD1") + ". Perbaiki dulu baris-baris ini, lalu tekan Kirim lagi."),
        h("table", { class: "table", style: "margin-top:12px" },
          h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama Barang"),
            h("th", { style: "text-align:right" }, "Kirim"), h("th", { style: "text-align:right" }, "Tersedia"),
            h("th", null, "Yang harus dilakukan"))),
          // Naming the wrong remedy matters: nothing on hand means the item must
          // leave the SJ, a partial means the quantity comes down to what exists.
          h("tbody", null, bad.map((x: any) => h("tr", null,
            h("td", null, h("b", null, x.item)),
            h("td", null, x.name || h("span", { class: "muted" }, "—")),
            numTd(x.kirim),
            numTd(x.avail, ";color:#b5453b;font-weight:700"),
            h("td", null, x.avail <= 0
              ? h("b", { style: "color:#b5453b" }, "Keluarkan item ini dari SJ")
              : h("span", null, "Ubah jumlah kirim menjadi ", h("b", null, fmt(x.avail)))))))),
        h("div", { class: "row", style: "justify-content:flex-end;margin-top:14px" },
          h("button", { class: "btn btn-primary", onClick: () => setShortfall(null) }, "Mengerti")),
      ]));
  };

  return h("div", { class: "main" }, Topbar({ title: "Surat Jalan", sub: "Kirim barang dari pusat ke outlet" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      shortfallModal,
      h("div", { class: "card" }, [
        h("h3", null, "Susun Surat Jalan"),
        h("div", { class: "hint", style: "margin-bottom:10px" },
          "Permintaan dan Pesanan digabung menjadi SATU Surat Jalan per cabang per hari. Bahan baku dibuat lepas dan boleh berkali-kali sehari. Draft belum memindahkan stok — jumlah masih bisa diubah sampai ditekan Kirim."),
        h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end" }, [
          field("Jenis Surat Jalan", h("select", { class: "input", value: kind,
            onChange: (e: any) => { setKind(e.currentTarget.value); setLines([]); } },
            SJ_KINDS.map((k) => h("option", { value: k.code }, k.label)))),
          () => manualKind() ? field("Outlet Tujuan", h("select", { class: "input", value: toBranch, onChange: (e: any) => setToBranch(e.currentTarget.value) },
            () => branches().map((b: any) => h("option", { value: b.code, selected: () => toBranch() === b.code },
              b.type === "pusat" ? b.name + " (Outlet Pusat)" : b.name)))) : null,
          () => manualKind() ? field("Gudang Asal", h("select", { class: "input", value: sourceGd, onChange: (e: any) => setSourceGd(e.currentTarget.value) },
            () => (gdList().length ? gdList() : ["GD1"]).map((g: string) => h("option", { value: g, selected: () => sourceGd() === g }, g)))) : null,
        ]),
        // One row per cabang with something waiting. The kind supplies the noun,
        // the detail column and the empty text (SJ_KINDS) — the shape is the
        // same for both, so it is written once.
        () => !manualKind() ? h("div", { style: "margin-top:12px" },
          srcList().length ? h("table", { class: "table" },
            h("thead", null, h("tr", null, h("th", null, "Cabang"),
              h("th", { style: "text-align:right" }, "Menunggu"),
              h("th", null, SJ_KIND[kind()].detailHead), h("th", null, ""))),
            h("tbody", null, () => branches().filter((b: any) => pendingBy()[b.code]).map((b: any) => h("tr", null,
              h("td", null, h("b", null, b.name), sjNote(b.code)),
              h("td", { style: "text-align:right" }, fmt(pendingBy()[b.code].length) + " " + SJ_KIND[kind()].word),
              h("td", { class: "muted", style: "font-size:12.5px" },
                pendingBy()[b.code].map(SJ_KIND[kind()].detailOf).join(", ")),
              h("td", { style: "text-align:right" },
                h("button", { class: "btn btn-primary", style: "padding:5px 12px;font-size:12.5px",
                  disabled: () => busy() || sjBlocked(b.code), onClick: () => susunSJ(b.code) }, "Susun SJ"))))))
            : h("div", { class: "muted" }, SJ_KIND[kind()].empty)) : null,
        // Bahan baku — the only kind whose lines are keyed in by hand.
        () => manualKind() ? h("div", { class: "row", style: "gap:12px;flex-wrap:wrap;align-items:flex-end;margin-top:12px" }, [
          field("Kode Item", ItemSelect({ value: item, onSelect: (c: string) => { setItem(c); loadAvail([c]); }, placeholder: "Cari kode / nama…" })),
          field("Jumlah", h("input", { class: "input", value: qty, onInput: (e: any) => setQty(e.currentTarget.value) })),
          h("button", { class: "btn", onClick: addLine }, "+ Item"),
          () => item() ? h("span", { class: "muted", style: "font-size:12.5px" }, "Stok " + item() + " di " + sourceGd() + ": " + fmt(availAt(item(), sourceGd()))) : null,
        ]) : null,
        () => manualKind() && lines().length ? h("table", { class: "table", style: "margin-top:12px" },
          h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama Barang"), h("th", null, "Satuan"),
            h("th", { style: "text-align:right" }, "Kirim"),
            h("th", { style: "text-align:right" }, "Tersedia"), h("th", null, ""))),
          h("tbody", null, () => lines().map((l: any) => h("tr", null,
            h("td", null, h("b", null, l.item)),
            h("td", null, l.name || metaOf(l.item).name || h("span", { class: "muted" }, "—")),
            h("td", null, l.unit || metaOf(l.item).unit || h("span", { class: "muted" }, "—")),
            h("td", { style: "text-align:right" },
              h("input", { class: "input", style: "width:100px;text-align:right;padding:5px 8px",
                value: () => String(l.qty), onInput: (e: any) => setLines(lines().map((x: any) => x.item === l.item ? { ...x, qty: e.currentTarget.value } : x)) })),
            h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" },
              h("span", { style: (+l.qty > availAt(l.item, sourceGd())) ? "color:#b5453b;font-weight:700" : "" }, fmt(availAt(l.item, sourceGd())))),
            h("td", { style: "text-align:right" },
              h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => setLines(lines().filter((x: any) => x.item !== l.item)) }, "Hapus")))))) : null,
        h("div", { class: "field", style: "margin-top:12px" }, h("label", null, "Catatan"),
          h("input", { class: "input", value: note, onInput: (e: any) => setNote(e.currentTarget.value) })),
        // Only bahan baku keeps a submit button here — the other two kinds are
        // assembled per cabang from the rows above.
        () => manualKind() ? h("button", { class: "btn btn-primary", disabled: busy, onClick: () => susunSJ(toBranch()) },
          () => busy() ? h("span", { class: "spin" }) : null, "Buat Surat Jalan Bahan Baku") : null,
      ]),
      // Drafts: the only place quantities can still change, because nothing has
      // moved yet. Kirim hands the pindahgd to the agent and locks the document.
      () => drafts().length ? h("div", { class: "card", style: "margin-top:18px" }, [
        h("h3", null, "Draft Surat Jalan — belum dikirim"),
        h("div", { class: "grid", style: "gap:12px" }, () => drafts().map((d: any) =>
          h("div", { style: "border:1px solid var(--line);border-radius:11px;padding:13px 15px" }, [
            h("div", { class: "row between" }, [
              h("div", null, h("b", null, d.sj_no), kindBadge(d.kind),
                h("div", { class: "muted", style: "font-size:12.5px;margin-top:3px" },
                  "→ " + d.to + " · dari " + (d.source_gd || "GD1") + " · " + d.when)),
              h("button", { class: "btn btn-primary", disabled: busy, onClick: () => sendDraft(d) },
                () => busy() ? h("span", { class: "spin" }) : null, "Kirim"),
            ]),
            h("table", { class: "table", style: "margin-top:10px" },
              h("thead", null, h("tr", null, h("th", null, "Item"), h("th", null, "Nama Barang"), h("th", null, "Sumber"),
                h("th", { style: "text-align:right" }, "Diminta"), h("th", { style: "text-align:right" }, "Kirim"),
                h("th", { style: "text-align:right" }, "Tersedia"), h("th", null, ""))),
              h("tbody", null, () => (d.lines || []).map((l: any) => h("tr", null,
                h("td", null, h("b", null, l.item)),
                h("td", null, l.name || h("span", { class: "muted" }, "—")),
                // A cabang's withdrawal ask outranks the pesanan reference here: it
                // is the one thing on this row that should stop a press of Kirim.
                l.retract === "pending"
                  ? h("td", { style: "font-size:12px;color:#A9781F;font-weight:600" }, "Cabang minta ditarik")
                  : h("td", { class: "muted", style: "font-size:12px" }, l.pesanan_no
                      ? l.pesanan_no + (l.pesanan_cust ? " · " + l.pesanan_cust : "") + (l.jenis_kirim ? " · " + l.jenis_kirim : "")
                      : "—"),
                h("td", { style: "text-align:right" }, +l.asked > 0 ? fmt(l.asked) : h("span", { class: "muted" }, "—")),
                h("td", { style: "text-align:right" },
                  h("input", { class: "input", style: "width:96px;text-align:right;padding:5px 8px",
                    value: () => String(l.qty), onChange: (e: any) => setLineQty(l.id, +e.currentTarget.value || 0) })),
                // Red on the ITEM's total across this SJ, not on this line alone
                // — that is what the send check measures, and aggregation makes
                // the same item arriving from two sources routine.
                h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" },
                  h("span", { style: (+l.claimed > +l.avail) ? "color:#b5453b;font-weight:700" : "" }, fmt(l.avail)),
                  (+l.claimed > +l.qty) ? h("div", { class: "muted", style: "font-size:11px" }, "SJ minta " + fmt(l.claimed)) : null),
                h("td", { style: "text-align:right" },
                  h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", onClick: () => setLineQty(l.id, 0) }, "Hapus"))))),
            ),
          ]))),
      ]) : null,
      h("div", { class: "card", style: "margin-top:18px" }, [
        h("div", { class: "row between", style: "align-items:center" },
          h("h3", { style: "margin:0" }, "Daftar Surat Jalan"),
          xlsxBtn(() => "SuratJalan-" + dstamp(),
            ["No SJ", "Status", "Ke Outlet", "Dari Gudang", "Tgl", "Jenis", "Kode", "Jumlah"],
            () => sent().flatMap((d: any) => (d.lines || []).map((l: any) => [d.sj_no, d.status, d.to, d.source_gd || "GD", d.when, kindLabel(d.kind), l.item, +l.qty || 0])))),
        h("div", { class: "filters", style: "margin:12px 0 14px" }, [
          h("select", { class: "input", onChange: (e: any) => { setFBranch(e.currentTarget.value); loadList(); } },
            h("option", { value: "" }, "Semua Outlet"), () => branches().map((b: any) => h("option", { value: b.code }, b.name))),
          h("select", { class: "input", onChange: (e: any) => { setFStatus(e.currentTarget.value); loadList(); } },
            h("option", { value: "" }, "Semua Status"),
            h("option", { value: "sent" }, "Dikirim"),
            h("option", { value: "received" }, "Diterima")),
          h("select", { class: "input", onChange: (e: any) => { setFKind(e.currentTarget.value); loadList(); } },
            h("option", { value: "" }, "Semua Jenis"),
            SJ_KINDS.map((k) => h("option", { value: k.code }, k.label))),
          h("input", { class: "input grow", placeholder: "Cari No. Surat Jalan…", value: fQ, onInput: (e: any) => { setFQ(e.currentTarget.value); loadList(); } }),
        ]),
        h("div", { class: "grid", style: "gap:12px" },
          () => sent().length ? sent().map((d: any) =>
            h("div", { style: "border:1px solid var(--line);border-radius:11px;padding:13px 15px" }, [
              h("div", { class: "row between" }, [
                h("div", null, h("b", null, d.sj_no), " ", statusBadge(d.status), kindBadge(d.kind),
                  h("div", { class: "muted", style: "font-size:12.5px;margin-top:3px" }, "→ " + d.to + " · dari " + (d.source_gd || "GD") + " · " + d.when)),
                h("button", { class: "btn btn-ghost", style: "padding:6px 12px", onClick: () => printSJ(d) }, "🖨 Cetak"),
              ]),
              h("div", { style: "margin-top:8px" }, () => (d.lines || []).map(lineBadge)),
            ]))
            : h("div", { class: "muted" }, "Belum ada surat jalan.")),
      ]),
    ]));
}

function LaporanPage() {
  const firstOfMonth = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-01"; };
  const todayStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
  const [from, setFrom] = createSignal(firstOfMonth());
  const [to, setTo] = createSignal(todayStr());
  const [branch, setBranch] = createSignal("");
  const [item, setItem] = createSignal("");
  const [data, setData] = createSignal<any>({ rows: [], total_qty: 0, count: 0 });
  const [branches, setBranches] = createSignal<any[]>([]);
  const [loading, setLoading] = createSignal(false);
  const isCabang = isOutletRole;
  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (from()) p.set("from", from());
    if (to()) p.set("to", to());
    if (!isCabang() && branch()) p.set("branch", branch());
    if (item()) p.set("item", item());
    try { setData((await api.get("/api/reports/produksi?" + p.toString())) || { rows: [] }); } catch (_) {}
    setLoading(false);
  };
  onMount(async () => {
    if (!isCabang()) { try { setBranches((await api.get("/api/branches")) || []); } catch (_) {} }
    load();
  });
  onLive(["stock", "requests"], load);
  const span = () => isCabang() ? 6 : 7;
  return h("div", { class: "main" }, Topbar({ title: "Laporan Produksi", sub: isCabang() ? "Produksi setengah jadi → jadi di outlet Anda" : "Ringkasan produksi per periode" }),
    h("div", { class: "content" }, [
      h("div", { class: "card" },
        h("div", { class: "filters", style: "align-items:flex-end" }, [
          field("Dari Tgl", h("input", { class: "input", type: "date", value: from, onInput: (e: any) => setFrom(e.currentTarget.value) })),
          field("Sampai Tgl", h("input", { class: "input", type: "date", value: to, onInput: (e: any) => setTo(e.currentTarget.value) })),
          () => !isCabang() ? field("Outlet", h("select", { class: "input", value: branch, onChange: (e: any) => setBranch(e.currentTarget.value) },
            h("option", { value: "" }, "Semua"), () => branches().map((b: any) => h("option", { value: b.code }, b.name)))) : null,
          field("Kode Barang", h("input", { class: "input", value: item, placeholder: "opsional", onInput: (e: any) => setItem(e.currentTarget.value.toUpperCase()) })),
          h("button", { class: "btn btn-primary", onClick: load }, "Tampilkan"),
          xlsxBtn(() => "LaporanProduksi-" + dstamp(),
            ["Tgl", "No Produksi", "Outlet", "Kode", "Nama", "Satuan", "Jumlah", "Jenis"],
            () => (data().rows || []).map((r: any) => [r.date, r.production_no, r.branch_name || r.branch, r.item, r.name, r.unit || "", +r.qty || 0, jenisProduksi(r)])),
        ])),
      h("div", { class: "card", style: "margin-top:18px;padding:6px 0 0;overflow-x:auto" },
        h("table", { class: "table" },
          h("thead", null, h("tr", null,
            h("th", { style: "padding-left:22px" }, "Tgl"), h("th", null, "No Produksi"),
            () => !isCabang() ? h("th", null, "Outlet") : null,
            h("th", null, "Kode"), h("th", null, "Nama"),
            h("th", { style: "text-align:right" }, "Jumlah"), h("th", { style: "text-align:right;padding-right:22px" }, "Jenis"))),
          h("tbody", null, () => (data().rows && data().rows.length)
            ? data().rows.map((r: any) => h("tr", null,
                h("td", { style: "padding-left:22px" }, r.date),
                h("td", null, h("b", null, r.production_no)),
                () => !isCabang() ? h("td", null, r.branch) : null,
                h("td", null, r.item),
                h("td", null, r.name || "—"),
                h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" }, fmt(r.qty)),
                h("td", { style: "text-align:right;padding-right:22px" }, jenisProduksiBadge(r))))
            : h("tr", null, h("td", { colspan: span(), class: "muted", style: "text-align:center;padding:30px" }, loading() ? "Memuat…" : "Tidak ada data untuk periode ini.")))),
      ),
      h("div", { class: "hint", style: "margin-top:10px" }, () => (data().count || 0) + " baris · total produksi " + fmt(data().total_qty || 0)),
    ]));
}

function PesananPage() {
  const [list, setList] = createSignal<any[]>([]);
  const [branches, setBranches] = createSignal<any[]>([]);
  const [flBranch, setFlBranch] = createSignal("");
  const [flStatus, setFlStatus] = createSignal("open");
  const [flQ, setFlQ] = createSignal("");
  // Pickup-date window. Empty = no bound; "Hari ini" sets both to today, which
  // is the case the client actually asked for ("pesanan dalam 1 hari").
  const [flFrom, setFlFrom] = createSignal("");
  const [flTo, setFlTo] = createSignal("");
  const [expanded, setExpanded] = createSignal<any>({});
  const isCabang = isOutletRole;
  let qTimer: any;
  const load = async () => {
    const p = new URLSearchParams();
    if (!isCabang() && flBranch()) p.set("branch", flBranch());
    p.set("status", flStatus());
    if (flFrom()) p.set("from", flFrom());
    if (flTo()) p.set("to", flTo());
    if (flQ()) p.set("q", flQ());
    try { setList(await api.get("/api/pemesanan?" + p.toString()) || []); } catch (_) {}
  };
  onMount(async () => {
    if (!isCabang()) { try { setBranches((await api.get("/api/branches")) || []); } catch (_) {} }
    await load();
  });
  onLive(["pemesanan"], load);
  const toggle = (id: number) => setExpanded({ ...expanded(), [id]: !expanded()[id] });
  const [msg, setMsg] = createSignal<any>(null);
  // Archive = close in central in a way the agent sync will NOT reopen — for
  // residual Delphi orders (years old, never picked up). Restorable per row.
  const setArchived = async (o: any, archived: boolean) => {
    setMsg(null);
    try { await api.send("/api/pemesanan/archive", "POST", { id: o.id, archived }); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const sweepPastDue = async () => {
    if (!window.confirm("Arsipkan SEMUA pesanan terbuka yang tanggal ambilnya sudah lewat" + (flBranch() ? " di outlet " + flBranch() : " di semua outlet") + "? Pesanan bisa dipulihkan satu per satu dari filter Diarsipkan.")) return;
    setMsg(null);
    try {
      const r = await api.send("/api/pemesanan/archive-pastdue", "POST", { branch: flBranch() });
      setMsg({ ok: true, t: (r.archived || 0) + " pesanan lewat tanggal diarsipkan." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };

  return h("div", { class: "main" }, Topbar({ title: "Pesanan Pelanggan", sub: isCabang() ? "Pesanan pelanggan outlet Anda" : "Pesanan pelanggan semua outlet" }),
    h("div", { class: "content" }, [
      h("div", { class: "card", style: "margin-bottom:14px" },
        h("div", { class: "filters" }, [
          () => !isCabang() ? field("Outlet", h("select", { class: "input", value: flBranch, onChange: (e: any) => { setFlBranch(e.currentTarget.value); load(); } },
            h("option", { value: "", selected: () => flBranch() === "" }, "Semua Outlet"),
            () => branches().map((b: any) => h("option", { value: b.code, selected: () => flBranch() === b.code }, b.name)))) : null,
          field("Status", h("select", { class: "input", value: flStatus, onChange: (e: any) => { setFlStatus(e.currentTarget.value); load(); } },
            h("option", { value: "open", selected: () => flStatus() === "open" }, "Terbuka"),
            h("option", { value: "closed", selected: () => flStatus() === "closed" }, "Selesai"),
            h("option", { value: "archived", selected: () => flStatus() === "archived" }, "Diarsipkan"),
            h("option", { value: "all", selected: () => flStatus() === "all" }, "Semua"))),
          field("Tgl Ambil dari", h("input", { class: "input", type: "date", value: flFrom,
            onChange: (e: any) => { setFlFrom(e.currentTarget.value); load(); } })),
          field("sampai", h("input", { class: "input", type: "date", value: flTo,
            onChange: (e: any) => { setFlTo(e.currentTarget.value); load(); } })),
          h("button", { class: "btn btn-ghost", style: "align-self:flex-end",
            onClick: () => { const t = todayISO(); setFlFrom(t); setFlTo(t); load(); } }, "Hari Ini"),
          h("button", { class: "btn btn-ghost", style: "align-self:flex-end",
            onClick: () => { setFlFrom(""); setFlTo(""); load(); } }, "Semua Tgl"),
          field("Cari", h("input", { class: "input", placeholder: "No PO / nama pelanggan…", value: flQ,
            onInput: (e: any) => { setFlQ(e.currentTarget.value); clearTimeout(qTimer); qTimer = setTimeout(load, 300); } })),
          xlsxBtn(() => "PesananPelanggan-" + (flBranch() || "semua") + "-" + dstamp(),
            ["No PO", "Outlet", "Tgl Pesan", "Tgl Ambil", "Pelanggan", "Telp", "Alamat", "Ambil/Kirim", "Keterangan", "Kode", "Nama Barang", "Qty", "Satuan", "Harga", "Status"],
            () => list().flatMap((o: any) => o.lines.map((l: any) =>
              [o.no_po, o.branch_name, o.tgl_po, o.tgl_ambil, o.cust_name, o.cust_phone, o.cust_addr || "", o.jenis_kirim || "", o.keterangan || "", l.item, l.name, +l.qty || 0, l.satuan, +l.harga || 0, o.archived ? "diarsipkan" : o.status]))),
          h("button", { class: "btn btn-ghost", style: "align-self:flex-end", title: "Arsipkan semua pesanan terbuka yang tanggal ambilnya sudah lewat (sisa data lama Delphi yang tidak pernah ditutup)", onClick: sweepPastDue }, "Arsipkan Lewat Tanggal"),
        ]),
        () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err"), style: "margin-top:10px" }, msg().t) : null),
      h("div", { class: "card" }, [
        h("table", { class: "table" },
          h("thead", null, h("tr", null,
            h("th", null, "No PO"), () => !isCabang() ? h("th", null, "Outlet") : null,
            h("th", null, "Tgl Ambil"), h("th", null, "Pelanggan"),
            h("th", { style: "text-align:right" }, "Nilai"), h("th", { style: "text-align:right" }, "DP"),
            h("th", null, "Status"), h("th", null, ""))),
          // expanded() is only read inside per-row accessors, so toggling one
          // row re-renders that row's arrow + detail — not the whole table.
          h("tbody", null, () => list().flatMap((o: any) => [
            h("tr", { style: "cursor:pointer", onClick: () => toggle(o.id) },
              h("td", null, h("b", null, o.no_po),
                h("div", { class: "muted", style: "font-size:12px" }, "pesan " + o.tgl_po)),
              !isCabang() ? h("td", null, o.branch_name) : null,
              h("td", null, o.tgl_ambil || "—",
                o.due_soon ? h("span", { class: "badge", style: "background:#F3E3B5;color:#7a5c17;margin-left:6px" }, "Segera") : null),
              h("td", null, o.cust_name,
                kirimBadge(o),
                o.cust_phone ? h("div", { class: "muted", style: "font-size:12px" }, o.cust_phone) : null),
              h("td", { style: "text-align:right" }, fmt(o.nilai)),
              h("td", { style: "text-align:right" }, fmt(o.uang_muka)),
              h("td", null,
                o.archived ? h("span", { class: "badge", style: "background:var(--surface-2);color:var(--ink-soft)", title: "Ditutup dari Central — data asal di POS tetap terbuka" }, "Diarsipkan") : statusBadge(o.status),
                o.sj_no ? h("span", { class: "badge cabang", style: "margin-left:6px", title: "Sudah dibuatkan Surat Jalan" }, o.sj_no) : null),
              h("td", { style: "white-space:nowrap;text-align:right" },
                (() => {
                  const archBtn = (label: string, flag: boolean, title?: string) =>
                    h("button", { class: "btn btn-ghost", style: "padding:4px 9px;font-size:12px", title,
                      onClick: (e: any) => { e.stopPropagation(); setArchived(o, flag); } }, label);
                  return o.archived ? archBtn("Pulihkan", false)
                    : o.status === "open" ? archBtn("Arsipkan", true, "Tutup pesanan lama ini di Central (POS tidak berubah)") : null;
                })(),
                h("span", { class: "muted", style: "font-size:12px;margin-left:8px" }, () => expanded()[o.id] ? "▾" : "▸ " + o.lines.length + " item"))),
            () => expanded()[o.id] ? h("tr", null, h("td", { colSpan: 9, style: "background:var(--bg,#FCF7F0);padding:8px 14px" },
              h("table", { class: "table", style: "margin:0" },
                h("thead", null, h("tr", null, h("th", null, "Kode"), h("th", null, "Nama Barang"),
                  h("th", { style: "text-align:right" }, "Qty"), h("th", null, "Satuan"), h("th", { style: "text-align:right" }, "Harga"))),
                h("tbody", null, o.lines.map((l: any) => h("tr", null,
                  h("td", null, h("b", null, l.item)), h("td", null, l.name),
                  h("td", { style: "text-align:right" }, fmt(l.qty)), h("td", null, l.satuan),
                  h("td", { style: "text-align:right" }, fmt(l.harga)))))),
              h("div", { class: "muted", style: "margin-top:6px;font-size:12.5px" },
                kirimLabel(o.jenis_kirim),
                o.cust_addr ? " · " + o.cust_addr : ""),
              o.keterangan ? h("div", { class: "muted", style: "margin-top:4px;font-size:12.5px" }, "Catatan: " + o.keterangan) : null)) : null,
          ]))),
        h("div", { class: "hint", style: "margin-top:10px" }, () => list().length + " pesanan · klik baris untuk rincian"),
      ]),
    ]));
}

// THE Mutasi Stock column spec, in the client's Check Stock.xlsx layout. The
// two-level header, the body, the totals row and the Excel export all derive
// from this, so a column can never appear in one and be missing from another.
//
// Rp is not stored: it is qty x unit_cost, computed the same way in all four.
//
// "Pakai Prod" (bahan baku consumed by produksi/finishing) is NOT on the
// client's sheet, but it is a real stock-out here, so it sits inside Keluar and
// is counted in Total. Without it Total would not explain Akhir, which is the
// one arithmetic the client checks: Akhir = Siap Jual - Total.
type MutSub = { t: string; k: string; rp?: 1; bold?: 1; diff?: 1; input?: 1; title?: string };
const PAKAI_PROD = "bahan yang dipakai produksi/finishing";
const MUT_GROUPS: { g: string; subs: MutSub[] }[] = [
  { g: "Stock Awal", subs: [{ t: "Qty", k: "awal" }, { t: "Rp", k: "awal", rp: 1 }] },
  { g: "Total Masuk", subs: [{ t: "Qty", k: "masuk" }, { t: "Rp", k: "masuk", rp: 1 }] },
  { g: "Stock Siap Jual", subs: [{ t: "Qty", k: "siapjual" }, { t: "Rp", k: "siapjual", rp: 1 }] },
  { g: "Keluar Qty", subs: [{ t: "Sales", k: "sales" }, { t: "BS", k: "bs" }, { t: "Compliment", k: "compliment" },
    { t: "Pakai Prod", k: "produksi", title: PAKAI_PROD }, { t: "Total", k: "keluar", bold: 1 }] },
  { g: "Keluar (Rp)", subs: [{ t: "Sales", k: "sales", rp: 1 }, { t: "BS", k: "bs", rp: 1 },
    { t: "Compliment", k: "compliment", rp: 1 }, { t: "Pakai Prod", k: "produksi", rp: 1, title: PAKAI_PROD },
    { t: "Total", k: "keluar", rp: 1, bold: 1 }] },
  { g: "Stock Akhir", subs: [{ t: "Qty", k: "akhir", bold: 1 }, { t: "Rp", k: "akhir", rp: 1 }] },
  { g: "Check", subs: [{ t: "Qty", k: "check", input: 1 }, { t: "Rp", k: "check", rp: 1 }] },
  { g: "Selisih", subs: [{ t: "Qty", k: "selisih", diff: 1 }, { t: "Rp", k: "selisih", rp: 1, diff: 1 }] },
];
const MUT_SUBS = MUT_GROUPS.flatMap((g) => g.subs);

function MutasiPage() {
  const firstOfMonth = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-01"; };
  const todayStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
  const [from, setFrom] = createSignal(firstOfMonth());
  const [to, setTo] = createSignal(todayStr());
  const [branch, setBranch] = createSignal("");
  const [gd, setGd] = createSignal("GD");
  const [rows, setRows] = createSignal<any[]>([]);
  const [branches, setBranches] = createSignal<any[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [msg, setMsg] = createSignal<any>(null);
  const [cutoff, setCutoff] = createSignal("");
  const [seeding, setSeeding] = createSignal(false);
  const [importing, setImporting] = createSignal(false);
  const [importResult, setImportResult] = createSignal<any>(null);
  const [flJenis, setFlJenis] = createSignal("");
  const [flQ, setFlQ] = createSignal("");
  const isCabang = isOutletRole;
  // Jenis + search are display-only filters over the already-loaded rows —
  // instant, no re-fetch (unlike date/outlet/gudang which change the query).
  const filtered = createMemo(() => {
    const j = flJenis(), q = flQ().trim().toLowerCase();
    return rows().filter((r: any) =>
      (!j || r.jenis === j) &&
      (!q || (r.code || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q)));
  });
  const load = async () => {
    setLoading(true); setMsg(null);
    const p = new URLSearchParams();
    p.set("from", from()); p.set("to", to());
    if (!isCabang() && branch()) p.set("branch", branch());
    if (gd()) p.set("gd", gd());
    try { const r = await api.get("/api/reports/mutasi?" + p.toString()); setRows(r?.rows || []); }
    catch (e: any) { setRows([]); setMsg({ ok: false, t: e.message }); }
    setLoading(false);
  };
  onMount(async () => {
    if (!isCabang()) { try { const bs = (await api.get("/api/branches")) || []; setBranches(bs); if (bs[0]) setBranch(bs[0].code); } catch (_) {} }
    await load();
  });
  onLive(["stock"], load);
  const saveCheck = async (r: any, val: string) => {
    if (!gd()) { setMsg({ ok: false, t: "Pilih satu Gudang untuk input opname (bukan Semua)." }); return; }
    try { await api.send("/api/opname", "POST", { branch: isCabang() ? "" : branch(), item: r.code, gd: gd(), date: to(), qty: +val || 0 }); await load(); }
    catch (e: any) { setMsg({ ok: false, t: e.message }); }
  };
  const setOpening = async () => {
    if (!cutoff()) { setMsg({ ok: false, t: "Pilih Tgl Cutoff untuk saldo awal." }); return; }
    if (!branch()) { setMsg({ ok: false, t: "Pilih Outlet." }); return; }
    setSeeding(true); setMsg(null);
    try {
      await api.send("/api/opname/recompute", "POST", { branch: branch(), cutoff: cutoff() });
      setMsg({ ok: true, t: "Perhitungan saldo awal per " + cutoff() + " dikirim ke agent outlet " + branch() + ". Saldo awal akan tersimpan setelah agent selesai menghitung dari riwayat Delphi (cek lagi beberapa saat)." });
    } catch (e: any) { setMsg({ ok: false, t: e.message }); }
    setSeeding(false);
  };
  const downloadTemplate = () => {
    if (!branch()) { setMsg({ ok: false, t: "Pilih Outlet." }); return; }
    if (!gd()) { setMsg({ ok: false, t: "Pilih satu Gudang (bukan Semua GD) untuk template opname." }); return; }
    if (!cutoff()) { setMsg({ ok: false, t: "Pilih Tgl Cutoff." }); return; }
    const url = "/api/opname/template?branch=" + encodeURIComponent(branch()) + "&gd=" + encodeURIComponent(gd()) + "&date=" + encodeURIComponent(cutoff());
    const a = document.createElement("a"); a.href = url; document.body.appendChild(a); a.click(); a.remove();
  };
  const importFile = async (file: any) => {
    if (!file) return;
    setImporting(true); setMsg(null); setImportResult(null);
    try {
      const j = await api.upload("/api/opname/import", file);
      setImportResult(j);
      setMsg({ ok: j.error_count === 0, t: "Import selesai — " + j.imported + " saldo awal tersimpan, " + j.error_count + " baris ditolak. Outlet " + j.branch + " / " + j.gd + " per " + j.date + "." });
      await load();
    } catch (e: any) { setMsg({ ok: false, t: "Import gagal: " + e.message }); }
    setImporting(false);
  };
  // unit_cost is Delphi tm_barang.HargaAvg, which is 0 for items never costed
  // there — those price at 0 rather than guessing a value.
  const val = (r: any, s: MutSub) => (+r[s.k] || 0) * (s.rp ? (+r.unit_cost || 0) : 1);
  // check/selisih arrive null until the item is counted (see reportMutasi's
  // outRow); a null column is blank everywhere and counts toward no total.
  const blank = (r: any, s: MutSub) => r[s.k] == null;
  const tot = (s: MutSub) => () => filtered().reduce((a: number, r: any) => a + (blank(r, s) ? 0 : val(r, s)), 0);
  // Check Qty is the only editable column (it IS the opname entry), and only
  // once a single gudang is chosen — an opname has to belong to one gudang.
  const mutCell = (r: any, s: MutSub) => {
    if (s.input && gd())
      return h("td", { style: "text-align:right" }, h("input", { class: "input", style: "width:74px;text-align:right;padding:4px 7px",
        value: () => r.check == null ? "" : String(r.check), onChange: (e: any) => saveCheck(r, e.currentTarget.value) }));
    if (blank(r, s)) return h("td", { style: "text-align:right;font-variant-numeric:tabular-nums" }, h("span", { class: "muted" }, "—"));
    const v = val(r, s), off = s.diff && Math.abs(v) > 0.0001;
    return numTd(v, (s.bold || off ? ";font-weight:700" : "") + (off ? ";color:#b5453b" : ""));
  };

  return h("div", { class: "main" }, Topbar({ title: "Mutasi Stock", sub: isCabang() ? "Mutasi stok outlet Anda" : "Mutasi stok per periode & outlet" }),
    h("div", { class: "content" }, [
      () => msg() ? h("div", { class: "alert " + (msg().ok ? "ok" : "err") }, msg().t) : null,
      h("div", { class: "card" },
        h("div", { class: "filters", style: "align-items:flex-end" }, [
          field("Dari Tgl", h("input", { class: "input", type: "date", value: from, onInput: (e: any) => setFrom(e.currentTarget.value) })),
          field("Sampai Tgl", h("input", { class: "input", type: "date", value: to, onInput: (e: any) => setTo(e.currentTarget.value) })),
          () => !isCabang() ? field("Outlet", h("select", { class: "input", value: branch, onChange: (e: any) => setBranch(e.currentTarget.value) },
            () => branches().map((b: any) => h("option", { value: b.code }, b.name)))) : null,
          field("Gudang", h("select", { class: "input", value: gd, onChange: (e: any) => setGd(e.currentTarget.value) },
            h("option", { value: "", selected: () => gd() === "" }, "Semua GD"),
            h("option", { value: "GD", selected: () => gd() === "GD" }, "GD (Outlet)"),
            h("option", { value: "GD1", selected: () => gd() === "GD1" }, "GD1 (Gudang Bawah)"),
            h("option", { value: "GDBB", selected: () => gd() === "GDBB" }, "GDBB"))),
          field("Jenis", h("select", { class: "input", value: flJenis, onChange: (e: any) => setFlJenis(e.currentTarget.value) }, jenisOptions(flJenis))),
          field("Cari Barang", h("input", { class: "input", placeholder: "kode / nama…", value: flQ, onInput: (e: any) => setFlQ(e.currentTarget.value) })),
          h("button", { class: "btn btn-primary", onClick: load }, "Tampilkan"),
          // Group folded into each header ("Stock Awal Qty") — one header row.
          xlsxBtn(() => "MutasiStock-" + (branch() || "") + "-" + from() + "_" + to(),
            ["Kode", "Nama Barang", "Jenis", ...MUT_GROUPS.flatMap((g) => g.subs.map((s) => g.g + " " + s.t))],
            () => filtered().map((r: any) => [r.code, r.name, jenisText(r.jenis),
              ...MUT_SUBS.map((s) => blank(r, s) ? "" : val(r, s))])),
        ])),
      () => !isCabang() ? h("div", { class: "card", style: "margin-top:12px;border-color:var(--brand-soft);background:#FFF8EF" }, [
        h("h3", { style: "margin:0 0 3px" }, "Set Saldo Awal (Opening Balance)"),
        h("div", { class: "hint", style: "margin:0 0 12px" }, "Go-live: pilih Outlet + Gudang + Tgl Cutoff (hari terakhir sebelum mulai pakai), unduh template, isi kolom SaldoAkhir dari hitung stok fisik, lalu Import. Import Excel MENIMPA nilai lama (boleh diulang setelah perbaikan)."),
        h("div", { class: "row", style: "align-items:flex-end;gap:12px;flex-wrap:wrap" }, [
          field("Tgl Cutoff", h("input", { class: "input", type: "date", value: cutoff, onInput: (e: any) => setCutoff(e.currentTarget.value) })),
          h("button", { class: "btn", onClick: downloadTemplate }, "⤓ Unduh Template Excel"),
          h("label", { class: "btn" + (importing() ? " disabled" : ""), style: "cursor:pointer" },
            () => importing() ? h("span", { class: "spin" }) : null, "⤒ Import Excel",
            h("input", { type: "file", accept: ".xlsx", style: "display:none",
              onChange: (e: any) => { const f = e.currentTarget.files && e.currentTarget.files[0]; e.currentTarget.value = ""; importFile(f); } })),
        ]),
        h("div", { class: "hint", style: "margin:12px 0 4px" }, "Opsional (perkiraan): hitung otomatis dari riwayat Delphi — bisa minus untuk item yang belum pernah diopname, jadi pakai hanya sebagai draft. Import Excel akan menimpanya."),
        h("button", { class: "btn btn-ghost", style: "padding:6px 12px;font-size:12.5px", disabled: seeding, onClick: setOpening }, () => seeding() ? h("span", { class: "spin" }) : null, "Hitung dari Riwayat (draft)"),
        importErrorList(importResult),
      ]) : null,
      h("div", { class: "hint", style: "margin:12px 2px 0" }, () => gd() ? "Kolom Check bisa diisi (opname) untuk gudang " + gd() + " per tanggal " + to() + "." : "Pilih satu Gudang untuk mengisi opname (Check)."),
      h("div", { class: "card", style: "margin-top:12px;padding:6px 0 0;overflow-x:auto" },
        h("table", { class: "table", style: "min-width:" + (MUT_SUBS.length * 78 + 290) + "px" },
          h("thead", null, [
            h("tr", null,
              h("th", { rowspan: 2, style: "padding-left:22px;vertical-align:bottom" }, "Kode"),
              h("th", { rowspan: 2, style: "vertical-align:bottom" }, "Nama Barang"),
              ...MUT_GROUPS.map((g) => h("th", { colspan: g.subs.length,
                style: "text-align:center;border-left:1px solid var(--line)" }, g.g))),
            h("tr", null, ...MUT_GROUPS.flatMap((g) => g.subs.map((s, i) => h("th", { title: s.title || "",
              style: "text-align:right;font-weight:500;font-size:11.5px" + (i === 0 ? ";border-left:1px solid var(--line)" : "") }, s.t)))),
          ]),
          h("tbody", null, () => filtered().length
            ? filtered().map((r: any) => h("tr", null,
                h("td", { style: "padding-left:22px" }, h("b", null, r.code)),
                h("td", { style: "white-space:nowrap" }, r.name || "—"),
                ...MUT_SUBS.map((s) => mutCell(r, s))))
            : h("tr", null, h("td", { colspan: MUT_SUBS.length + 2, class: "muted", style: "text-align:center;padding:30px" },
                loading() ? "Memuat…" : (rows().length ? "Tidak ada barang cocok filter." : "Tidak ada data untuk periode ini.")))),
          // Check is deliberately not totalled: it would sum only the counted
          // items and sit beside an Akhir total over every row, inviting a
          // reconciliation against a gap that is only the un-counted items.
          () => filtered().length ? h("tfoot", null, h("tr", null,
            h("td", { colspan: 2, style: "padding-left:22px;font-weight:700" }, "Total"),
            ...MUT_SUBS.map((s) => s.k === "check" ? h("td", null, "") : numTd(tot(s), ";font-weight:700")))) : null)),
    ]));
}

function Placeholder(title: string) {
  return h("div", { class: "main" }, Topbar({ title }),
    h("div", { class: "content" }, h("div", { class: "card" },
      h("h3", null, title), h("p", { class: "muted" }, "Halaman ini akan segera hadir."))));
}

/* ---------------- forced password change (retires default admin cred) ---------------- */
function ForcePwChange() {
  const [cur, setCur] = createSignal("");
  const [pw, setPw] = createSignal("");
  const [pw2, setPw2] = createSignal("");
  const [err, setErr] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const submit = async (e: Event) => {
    e.preventDefault(); setErr("");
    if (pw().length < 6) { setErr("Kata sandi baru minimal 6 karakter."); return; }
    if (pw() !== pw2()) { setErr("Konfirmasi kata sandi tidak cocok."); return; }
    setBusy(true);
    try {
      await api.send("/api/me/password", "POST", { current: cur(), new: pw() });
      await loadMe(); // must_change_pw now cleared -> Shell renders
    } catch (er: any) { setErr(er.message || "Gagal mengubah kata sandi."); }
    setBusy(false);
  };
  return h("div", { class: "login-wrap" },
    h("form", { class: "login-card", onSubmit: submit }, [
      h("div", { class: "brand" }, h("span", { class: "mark" }, IcLock()),
        h("span", { class: "name" }, () => settings().company_name || "Ajeng Bakery", h("small", null, "Keamanan"))),
      h("h2", null, "Ganti Kata Sandi"),
      h("div", { class: "sub" }, "Demi keamanan, kata sandi bawaan harus diganti sebelum melanjutkan."),
      () => err() ? h("div", { class: "alert err" }, err()) : null,
      h("div", { class: "field" }, h("label", null, "Kata Sandi Saat Ini"),
        h("input", { class: "input", type: "password", value: cur, autofocus: true, onInput: (e: any) => setCur(e.currentTarget.value) })),
      h("div", { class: "field" }, h("label", null, "Kata Sandi Baru"),
        h("input", { class: "input", type: "password", value: pw, onInput: (e: any) => setPw(e.currentTarget.value) })),
      h("div", { class: "field" }, h("label", null, "Konfirmasi Kata Sandi Baru"),
        h("input", { class: "input", type: "password", value: pw2, onInput: (e: any) => setPw2(e.currentTarget.value) })),
      h("button", { class: "btn btn-primary", style: "width:100%;margin-top:6px", disabled: busy },
        () => busy() ? h("span", { class: "spin" }) : null, "Simpan & Lanjut"),
      h("a", { class: "muted", href: "#", style: "display:block;text-align:center;margin-top:12px;font-size:12.5px",
        onClick: doLogout }, "Keluar"),
    ]));
}

/* ---------------- login ---------------- */
function Login() {
  const [u, setU] = createSignal("");
  const [p, setP] = createSignal("");
  const [err, setErr] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const submit = async (e: Event) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.send("/api/login", "POST", { username: u(), password: p() });
      await Promise.all([loadMe(), loadSettings()]);
    } catch (er: any) { setErr("Nama pengguna atau kata sandi salah."); }
    setBusy(false);
  };
  return h("div", { class: "login-wrap" },
    h("form", { class: "login-card", onSubmit: submit }, [
      h("div", { class: "brand" }, BrandMark(),
        h("span", { class: "name" }, () => settings().company_name || "Ajeng Bakery", h("small", null, "Central"))),
      h("h2", null, "Selamat Datang"),
      h("div", { class: "sub" }, "Masuk untuk melanjutkan"),
      () => err() ? h("div", { class: "alert err" }, err()) : null,
      h("div", { class: "field" }, h("label", null, "Nama Pengguna"),
        h("input", { class: "input", value: u, autofocus: true, onInput: (e: any) => setU(e.currentTarget.value) })),
      h("div", { class: "field" }, h("label", null, "Kata Sandi"),
        h("input", { class: "input", type: "password", value: p, onInput: (e: any) => setP(e.currentTarget.value) })),
      h("button", { class: "btn btn-primary", style: "width:100%;margin-top:6px", disabled: busy },
        () => busy() ? h("span", { class: "spin" }) : null, "Masuk"),
    ]));
}

/* ---------------- shell + routing ---------------- */
function Shell() {
  return h("div", { class: "app" }, Sidebar(), () => {
    switch (route()) {
      case "/settings": return Settings();
      case "/users": return isAdminRole() ? UsersPage() : Dashboard();
      case "/requests": return PermintaanPage();
      case "/pesanan": return PesananPage();
      case "/produksi": return ProduksiPage();
      case "/suratjalan": return SuratJalanPage();
      case "/terima": return TerimaPage();
      case "/laporan": return LaporanPage();
      case "/mutasi": return MutasiPage();
      case "/recipes": return RecipesPage();
      case "/thresholds": return ThresholdsPage();
      case "/branches": return isAdminRole() ? BranchesPage() : Dashboard();
      case "/stock": return PosisiStok();
      default: return Dashboard();
    }
  });
}

function Root() {
  const [ready, setReady] = createSignal(false);
  onMount(async () => { await Promise.all([loadMe(), loadSettings()]); setReady(true); });
  return h("div", null, () => {
    if (!ready()) return h("div", { class: "login-wrap" }, h("span", { class: "spin" }));
    if (!me()) return Login();
    if (me().must_change_pw) return ForcePwChange();
    startLive();
    return Shell();
  });
}

render(() => h(Root, null), document.getElementById("app")!);
